import { CharacterInputValue } from '@tencent/tds-ui';
import { trpc } from '@tencent/tpro_rmonitor_web_server_url_token';
import { isEmpty } from 'lodash';
import { has, isString, unset } from 'lodash-es';
import moment from 'moment';
import { TRPCPost } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { getParamFromUrl } from '@/infrastructure/history';
import { logger } from '@/infrastructure/logger';
import { isElectron, isAppleTV, isIOS, isVisionOS } from '@/infrastructure/product-info';
import { deleteUndefinedOrNullProperties } from '@/infrastructure/utils/issue';
import { formatTimeRange, getDateRangeByShortCut } from '@/infrastructure/utils/timestamp';
import { toArray, toNumArray } from '@/infrastructure/utils/to-array';
import {
  BuglyFiltersName,
  getCompareBuglyFilter,
  getWildcardOperator,
  parseWildcardValue,
  replaceWildcardValue,
} from '@/presentation/components/exception-form/utils';
import { GroupName } from '@/presentation/components/form/components/select-group';
import { NumberOperator, NumberType } from '@/presentation/components/form/components/select-number';
import { TimeRangeGroupName } from '@/presentation/components/form/components/time-range-cascade';
import { VersionType } from '@/presentation/components/form/components/version-select';
import { VersionSelectGroupName } from '@/presentation/components/form/components/version-select-group';
import { FieldsOperator } from '@/presentation/components/form/proto';
import { MatchMode } from '@/presentation/components/form/standards/types';
import { generateFormList } from '@/presentation/components/problem-detail-filter';
import { CompareParams } from '@/presentation/components/tds-form';
import { AreaFieldValue, AreaNameEnum } from '@/presentation/components/tds-form/tds-area';
import { getTagList } from '@/presentation/pages/setting/tag-manage/api';
import {
  AppVersionOperator,
  CommonFieldKey,
  ComplianceFilter,
  FIELD_ALL,
  FieldKey,
} from '@/proto/field';
import { WithTypeCast } from '@raft/utils';
import { toCamelCase, toHumpString } from '@raft/utils/src/case/camel-case';
import { toLineString, toSnakeCase } from '@raft/utils/src/case/snake-case';

export type IFilter = WithTypeCast<trpc.rmonitor.common.IFilter>;
type IFormList = WithTypeCast<trpc.rmonitor.web_server.IFormList>;


export enum KeyDataType {
  NUMBER = 0,
  STRING = 1,
  ARRAY = 2,
  MAP = 3,
}

export type CustomField = {
  operator?: string;
  key?: string;
  values?: string[] | string | number;
  type?: KeyDataType;
};

export const getFilterKeyForCustomTag = (tag: FieldKey) => {
  const map: Record<string, string> = {
    [FieldKey.ShiplyTag]: 'a11',
    [FieldKey.AbTestTag]: 'a12',
  };

  return map[tag] ?? '';
};

export type FieldList = IFormList & {
  [FieldKey.StandardArea]?: AreaFieldValue[];
  [FieldKey.IsInferenceFeature]?: string;
  [FieldKey.OriginalStacks]?: string;
  [FieldKey.StandardInferredLabels]?: CharacterInputValue;
  // table form
  costTimeAvg?: any;
  // table form end
  [FieldKey.StandardStack]?: {
    mode?: string;
    values?: string[];
  },
  [FieldKey.TrafficSize]?: {
    operator?: string;
    value?: string[];
  },
  [FieldKey.StandardDetectStrategy]?: CharacterInputValue,
  [FieldKey.StandardRom]?: CharacterInputValue,
  [FieldKey.StandardAbTestTag]?: CharacterInputValue,
  [FieldKey.StandardTagsInFilter]?: CharacterInputValue,
  [FieldKey.StandardTimeCost]?: any,
  [FieldKey.StandardLaunchDuration]?: any,
  [FieldKey.StandardShiplyTag]?: CharacterInputValue,
  [FieldKey.StandardAppVersion]?: CharacterInputValue,
  [FieldKey.StandardSystemStack]?: string[],
  [FieldKey.StandardMonitoredThreadName]?: CharacterInputValue,
  [FieldKey.StandardBundleId]?: CharacterInputValue,
  [FieldKey.StandardChannelId]?: CharacterInputValue,
  [FieldKey.StandardFuncName]?: CharacterInputValue,
  [FieldKey.StandardFeature]?: CharacterInputValue,
  [FieldKey.StandardClientIdentify]?: CharacterInputValue,
  [FieldKey.StandardProcessLaunchId]?: CharacterInputValue,
  [FieldKey.StandardLaunchId]?: CharacterInputValue,
  [FieldKey.StandardQimei]?: CharacterInputValue,
  [FieldKey.StandardAccountId]?: CharacterInputValue,
  [FieldKey.StandardPlatform]?: CharacterInputValue,
  [FieldKey.StandardBuildNumber]?: CharacterInputValue,
  [FieldKey.StandardModel]?: CharacterInputValue,
  [FieldKey.StandardOsVersion]?: CharacterInputValue,
  [FieldKey.StandardProcessName]?: CharacterInputValue,
  [FieldKey.StandardScene]?: CharacterInputValue,
  [FieldKey.StandardSdkVersion]?: CharacterInputValue,
  [FieldKey.StandardBrand]?: CharacterInputValue,
  [FieldKey.StandardGround]?: string[],
  [FieldKey.StandardVersionMode]?: CharacterInputValue,
  [FieldKey.BundleId]?: string;
  [FieldKey.ChannelId]?: string;
  [FieldKey.AppVersionMode]?: string;
  [FieldKey.ShiplyTag]?: string;
  shiplyTagCompare?: string;
  abTestTagCompare?: string;
  // build_numberCompare?: string[];
  [FieldKey.AbTestTag]?: string;
  [FieldKey.TrafficType]?: string;
  [FieldKey.Operator]?: string;
  [FieldKey.ParentTrafficSize]?: string[];
  [FieldKey.ExceptionType]?: string;
  [FieldKey.Brand]?: string;
  [FieldKey.AppVersion]?: VersionType;
  [FieldKey.OsVersion]?: VersionType;
  [FieldKey.FuzzyMatch]?: string;
  [FieldKey.BackTime]?: string[];
  [FieldKey.BackTimeFilterType]?: string;
  [FieldKey.FilterSceneProblem]?: string;
  [FieldKey.Stack]?: string;
  [FieldKey.CoveredStatus]?: string;
  [GroupName.privacyFunction]?: string[];
  [GroupName.privacyFunctionFilter]?: string;
  [GroupName.coveredStatus]?: string[];
  [GroupName.coveredStatusFilter]?: string;
  [GroupName.problemType]?: string[];
  [GroupName.problemTypeFilter]?: string;
  [FieldKey.UserCustom]?: CustomField[];
  [FieldKey.BizExtendInfo]?: CustomField[];
  [FieldKey.StandardTrafficSizeType]?: CustomField[];
  [GroupName.fuzzyMatch]?: string;
  [GroupName.fuzzyMatchFilter]?: string;
  [FieldKey.AvgMaxMallocSize]?: string[];
  [FieldKey.AvgMaxMallocSizeCompare]?: string[];
  [FieldKey.AvgMallocSizeCompare]?: string[]
  [FieldKey.AvgMallocSize]?: string[];
  [FieldKey.MaxMallocSize]?: string[];
  [FieldKey.MaxMallocSizeCompare]?: string[];
  [FieldKey.UsedMem]?: string[];
  [FieldKey.UsedMemCompare]?: string[];
  [FieldKey.QimeiCount]?: string[];
  [FieldKey.QimeiCountCompare]?: string[];
  [FieldKey.DocCount]?: string[];
  [FieldKey.DocCountCompare]?: string[];
  [FieldKey.P90MaxMallocSize]?: string[];
  [FieldKey.P90MaxMallocSizeCompare]?: string[];
  [FieldKey.P50MaxMallocSize]?: string[];
  [FieldKey.P50MaxMallocSizeCompare]?: string[];
  [FieldKey.P90MallocSize]?: string[];
  [FieldKey.P90MallocSizeCompare]?: string[];
  [FieldKey.P50MallocSize]?: string[];
  [FieldKey.P50MallocSizeCompare]?: string[];
  [FieldKey.MallocSize]?: string[];
  [FieldKey.MallocSizeCompare]?: string[];
  [FieldKey.Operator]?: string;
  [FieldKey.Area]?: string;
  [FieldKey.FrontState]?: string[];
  [FieldKey.NetType]?: string[];
  minTrafficSize?: string;
  maxTrafficSize?: string;
  minParentTrafficSize?: string;
  maxParentTrafficSize?: string;
  bundleId?: string;
  appVersionMode?: string;
  isCompareOpen?: boolean;
  compareStatus?: number;
  topData?: string;
  timeType?: 'report' | 'occur';
  timeQuick?: string;
  appVersionGroup?: string;
  minAppVersion?: string;
  maxAppVersion?: string;
  wildcardAppVersion?: string;
  appVersionOperator?: AppVersionOperator;
  launchId?: string;
  processLaunchId?: string;
  exitReason?: string;
  exitDescription?: string;
  filePath?: string[];
  timeWindow?: number;
  smModule?: string;
  smPermission?: string;
  smScene?: string;
  smControl?: string;
  [FieldKey.CollectType]?: string;
  [FieldKey.Domain]?: string;
  [FieldKey.ReportType]?: string;
  launchCost?: string[];
  spanCost?: string[];
  spanNames?: string[];
  launchType?: string[];
  launchTag?: string[];
  [FieldKey.Linkages]?: string[];
  [FieldKey.FdCount]?: NumberType;
  [FieldKey.SuspiciousCount]?: NumberType;
  [FieldKey.PeerName]?: string;
  keyMethod?: string | null;
  taskId?: string;
  subIssueId?: string | null;
  exceptionTag?: string[];
  exceptionFilter?: string;
  [FieldKey.Scenes]?: string;
  [GroupName.hostType]?: string[];
  [GroupName.hostTypeFilter]?: string;
  tagColor?: string[];
  [FieldKey.ViewTag]?: string[];
  buildNumber?: string[];
  romName?: string[]
  [FieldKey.Api]?: string[];
  [FieldKey.Perm]?: string[];
  [FieldKey.Mime]?: string[];
  [FieldKey.AttributionSdk]?: string[];
};

export type CompareFieldList = FieldList & {
  [FieldKey.IsInferenceFeatureCompare]?: string;
  [FieldKey.StandardStackCompare]?: {
    mode?: string;
    values?: string[];
  };
  tagColorCompare?: string[];
  tagColor?: string[];
  [FieldKey.StandardDetectStrategyCompare]?: CharacterInputValue,
  [FieldKey.StandardShiplyTagCompare]?: CharacterInputValue,
  [FieldKey.StandardAppVersionCompare]?: CharacterInputValue,
  [FieldKey.StandardSystemStackCompare]?: string,
  [FieldKey.StandardMonitoredThreadNameCompare]?: CharacterInputValue,
  [FieldKey.StandardBundleIdCompare]?: CharacterInputValue,
  [FieldKey.StandardChannelIdCompare]?: CharacterInputValue,
  [FieldKey.StandardFuncNameCompare]?: CharacterInputValue,
  [FieldKey.StandardFeatureCompare]?: CharacterInputValue,
  [FieldKey.StandardClientIdentifyCompare]?: CharacterInputValue,
  [FieldKey.StandardProcessLaunchIdCompare]?: CharacterInputValue,
  [FieldKey.StandardLaunchIdCompare]?: CharacterInputValue,
  [FieldKey.StandardQimeiCompare]?: CharacterInputValue,
  [FieldKey.StandardAccountIdCompare]?: CharacterInputValue,
  [FieldKey.StandardPlatformCompare]?: CharacterInputValue,
  [FieldKey.StandardModelCompare]?: CharacterInputValue,
  [FieldKey.StandardOsVersionCompare]?: CharacterInputValue,
  [FieldKey.StandardProcessNameCompare]?: CharacterInputValue,
  [FieldKey.StandardSceneCompare]?: CharacterInputValue,
  [FieldKey.StandardSdkVersionCompare]?: CharacterInputValue,
  [FieldKey.StandardBrandCompare]?: CharacterInputValue,
  [FieldKey.StandardGroundCompare]?: string[],
  [FieldKey.StandardVersionModeCompare]?: CharacterInputValue,
  [FieldKey.AppVersionCompare]?: VersionType;
  [FieldKey.OsVersionCompare]?: VersionType;
  [FieldKey.UserCustomCompare]?: CustomField[];
  [FieldKey.BizExtendInfoCompare]?: CustomField[];
  [FieldKey.AbTestTagLine]?: CharacterInputValue;
  [FieldKey.StandardBuildNumberCompare]?: CharacterInputValue,
  [FieldKey.StandardRomCompare]?: CharacterInputValue,
  DocCountCompare?: string[];
  DocCount?: string[];
  MaxMallocSize?: string[];
  MaxMallocSizeCompare?: string[];
  appVersionMode?: string;
  appVersionModeCompare?: string;
  compareStatus?: number;
  customDateCompare?: string;
  issueStatusCompare?: string[];
  modelCompare?: string;
  sceneCompare?: string;
  sdkVersionCompare?: string;
  showNewCompare?: boolean;
  tagsCompare?: string[];
  tapdStatusCompare?: string[];
  timeRangeCompare?: string[];
  accountIdCompare?: string;
  qimeiCompare?: string;
  clientIdentifyCompare?: string;
  featureCompare?: string;
  funcNameCompare?: string;
  processNameCompare?: string,
  tapdHandlerCompare?: string[];
  tagFilterCompare?: string[];
  topNumCompare?: number;
  bundleIdCompare?: string;
  timeTypeCompare?: 'report' | 'occur';
  timeQuickCompare?: string;
  appVersionCompare?: string;
  appVersionGroupCompare?: string;
  minAppVersionCompare?: string;
  maxAppVersionCompare?: string;
  wildcardAppVersionCompare?: string;
  appVersionOperatorCompare?: AppVersionOperator;
  // 卡顿
  costTimeAvgMinCompare?: number;
  costTimeAvgMaxCompare?: number;
  groundCompare?: string;
  // java
  leakBytesMinCompare?: number;
  leakBytesMaxCompare?: number;
  // fd
  fdTypeCompare?: string;
  // 大图
  showTypeCompare?: string;
  activityNameCompare?: string;
  // 最大分配堆栈均值
  [FieldKey.AvgMaxMallocSizeCompare]?: string[];
  // 分配大小均值
  [FieldKey.AvgMallocSizeCompare]?: string[];

  launchIdCompare?: string;
  channelIdCompare?: string;
  buildNumberCompare?: string[];
  romNameCompare?: string[]
  processLaunchIdCompare?: string;
};

export const genStandardCompareToken = async (params: CompareParams): Promise<string> => {
  const formListA = toSnakeCase({
    ...params.fieldsValue,
    filters: params.fieldsFilters,
    timeWindow: params?.fieldsValue?.time_window_type,
  });

  const formListB = toSnakeCase({
    ...params.compareFieldsValue,
    filters: params.compareFieldsFilters,
  });

  try {
    // token不支持传多个formList，传了解析的时候也不会返回，所以把compareFormList放在formList里面
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/get`, toSnakeCase({
      formList: {
        ...formListA,
        isCompare: true,
      },
      formListA,
      formListB,
    }));

    return resp.token;
  } catch (e) {
    logger.error('genStandardToken err', e);
    return '';
  }
};

export const parseStandardCompareToken = async (token: string) => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/parse`, { token });
    const formList = resp.form_list;

    return {
      ...formList,
      isCompare: !!formList.is_compare,
      formListA: resp?.form_list_a,
      formListB: resp?.form_list_b,
    };
  } catch (e) {
    logger.error('parseStandardToken err', e);
    return {};
  }
};

export const genStandardToken = async (params: Record<string, any>): Promise<string> => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/get`, toSnakeCase({
      formList: params,
    }));

    return resp.token;
  } catch (e) {
    logger.error('genStandardToken err', e);
    return '';
  }
};

export const parseStandardToken = async (token: string) => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/parse`, { token });
    return resp.form_list;
  } catch (e) {
    logger.error('parseStandardToken err', e);
    return {};
  }
};

export const getToken = async (params: FieldList): Promise<string> => {
  try {
    const filters = getFilter(params);
    console.log('getToken', params, filters);
    const req = trpc.rmonitor.web_server.GetTokenReq.fromObject({
      formList: {
        ...params,
        tag_color: params.tagColor?.join?.(','),
        filters,
        tagList: params.tags,
        newTagList: params.tags ? await getTagId(params.tags!) : [],
        tags: params.tags || params.tagColor ? await getTagParams(params.tags!, params.tagColor!) : [],
        tapdHandler: toArray(params.tapdHandler!),
        newTagListOperator: params.newTagListOperator,
        tapdHandlerOperator: params.tapdHandlerOperator,
        issueStatusOperator: params.issueStatusOperator,
        tapdStatusOperator: params.tapdStatusOperator,
        tagColorOperator: params.tagColorOperator,
      },
    });
    const parseField = {
      timeQuick: params.timeQuick,
      timeType: params.timeType,
      minAppVersion: params.minAppVersion,
      maxAppVersion: params.maxAppVersion,
      wildcardAppVersion: params.wildcardAppVersion,
      appVersionOperator: params.appVersionOperator,
      timeWindow: params.timeWindow,
      standardStack: params.standardStack,
      standardTrafficSizeType: params.standardTrafficSizeType,
    };
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/get`, toSnakeCase({
      formList: {
        ...req.formList,
        ...parseField,
      },
    }));
    const respHump = trpc.rmonitor.web_server.GetTokenRsp.fromObject(toCamelCase(resp));
    return respHump.token;
  } catch (e) {
    logger.error('GetToken err', e);
    return '';
  }
};


export const parseAnaToken = async (token: string) => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/parse`, { token });
    const respHump: any = trpc.rmonitor.web_server.ParseTokenRsp.fromObject(toCamelCase(resp));
    const filter = parseFilter(respHump.formList!, true);

    const userCustomFilter = parseCustomFilter(respHump.formList!);
    const bizFilters = parseBizFilter(respHump.formList!);
    const param = {
      ...respHump.formList,
      ...filter,
      ...toCamelCase(resp.form_list?.parse_field ?? {}),
      detail: resp.form_list?.detail,
      userCustom: userCustomFilter,
      bizExtendInfo: bizFilters,
    };
    if (resp.form_list?.is_participle) {
      param.isParticiple = resp.form_list?.is_participle;
    }
    if (resp.form_list?.detail_mode) {
      param.detailMode = resp.form_list?.detail_mode;
    }
    return param;
  } catch (e) {
    logger.error('ParseToken err', e);
    return {};
  }
};

export const parseToken = async (token: string) => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/parse`, { token });

    const respHump: any = trpc.rmonitor.web_server.ParseTokenRsp.fromObject(toCamelCase(resp));
    const filter = parseFilter({
      ...toCamelCase(resp).formList,
      ...respHump.formList!,
    });
    const userCustomFilter = parseCustomFilter(respHump.formList!);
    const bizFilters = parseBizFilter(respHump.formList!);

    return {
      ...respHump.formList,
      ...filter,
      userCustom: userCustomFilter,
      bizExtendInfo: bizFilters,
    };
  } catch (e) {
    logger.error('ParseToken err', e);
    return {};
  }
};

// 性能对比模式获取token
export const getCompareToken = async (params: CompareFieldList): Promise<string> => {
  try {
    const [filtersA = [], filtersB = []] = getCompareFilters(params);
    const tags = await getTagParams(params.tags!, params.tagColor!);
    const tagsCompare = await getTagParams(params.tagsCompare!, params.tagColorCompare!);
    const getTapdHandler = (tapdHandler: string | string[]) => (Array.isArray(tapdHandler) ? tapdHandler : [tapdHandler || '']);
    const topData = params?.topData === FIELD_ALL ? undefined : Number(params.topData);
    const getTopNum = (topNum: number) => (isNaN(Number(topNum)) ? undefined : Number(topNum));
    unset(params, FieldKey.AppVersion);
    unset(params, FieldKey.OsVersion);
    unset(params, FieldKey.Model);
    const req = {
      formList: {
        ...params,
        tag_color: params.tagColor?.join?.(','),
        filters: filtersA,
        tagList: params.tags,
        tags,
        tapdHandler: getTapdHandler(params.tapdHandler!),
        topNum: isString(params.topNum) ? Number(params.topNum) : params.topNum,
      },
      formListA: {
        filters: filtersA,
        tagList: params.tags,
        tags: tags || [],
        tapdStatus: params.tapdStatus,
        tapdHandler: getTapdHandler(params.tapdHandler!),
        issueStatus: params.issueStatus,
        tagFilter: params.tagFilter ?? 'OR',
        tag_color: params.tagColor?.join?.(','),
        showNew: params.showNew,
        topNum: getTopNum(params.topNum) || topData,
        // 卡顿
        cost_time_avg_min: params?.costTimeAvgMin,
        cost_time_avg_max: params?.costTimeAvgMax,
        // java
        leak_bytes_min: params?.leakBytesMin,
        leak_bytes_max: params?.leakBytesMax,
      },
      formListB: {
        filters: filtersB,
        tagList: params.tagsCompare,
        tags: tagsCompare || [],
        tapdStatus: params.tapdStatusCompare,
        tapdHandler: getTapdHandler(params.tapdHandlerCompare!),
        issueStatus: params.issueStatusCompare,
        tagFilter: params.tagFilterCompare ?? 'OR',
        tag_color: params.tagColorCompare?.join(','),
        showNew: params.showNewCompare,
        topNum: getTopNum(params.topNumCompare!) || topData,
        // 卡顿
        cost_time_avg_min: params?.costTimeAvgMinCompare,
        cost_time_avg_max: params?.costTimeAvgMaxCompare,
        // java
        leak_bytes_min: params?.leakBytesMinCompare,
        leak_bytes_max: params?.leakBytesMaxCompare,
      },
    };

    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/get`, toSnakeCase(req));
    const respHump = trpc.rmonitor.web_server.GetTokenRsp.fromObject(toCamelCase(resp));
    return respHump.token;
  } catch (e) {
    logger.error('getCompareToken err', e);
    return '';
  }
};

// 性能对比模式解析token
export const parseCompareToken = async (token: string) => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/parse`, { token });
    const respHump = toCamelCase(resp);
    const filter = parseFilter(respHump.formList!, false);
    const compareFilter = parseCompareFilter(respHump.formList!);
    const userCustomFilter = parseCustomFilter(respHump.formList!);
    const userCompareCustomFilter = parseCompareCustomFilter(respHump.formList!);
    const bizFilters = parseBizFilter(respHump.formList!);
    const bizFiltersCompare = parseCompareBizFilter(respHump.formList!);

    return {
      ...respHump.formList,
      ...filter,
      ...compareFilter,
      userCustom: userCustomFilter,
      userCustomCompare: userCompareCustomFilter,
      bizExtendInfo: bizFilters,
      bizFiltersCompare,
      standards: resp.form_list,
    };
  } catch (e) {
    logger.error('parseCompareToken err', e);
    return {};
  }
};


// 用于hooks获取token数据
export const parseoHooksToken = async (token: string) => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/parse`, { token });
    const respHump = toCamelCase(resp);
    const filter = parseFilter(respHump.formList!);
    const compareFilter = parseCompareFilter(respHump.formList!);
    const userCustomFilter = parseCustomFilter(respHump.formList!);
    const userCompareCustomFilter = parseCompareCustomFilter(respHump.formList!);
    return deleteUndefinedOrNullProperties({
      ...respHump.formList,
      ...filter,
      ...compareFilter,
      userCustom: userCustomFilter,
      userCustomCompare: userCompareCustomFilter,
    });
  } catch (e) {
    logger.error('parseCompareToken err', e);
    return {};
  }
};


export const getBuglyToken = async (params: any): Promise<string> => {
  try {
    const req = {
      formList: params,
    };
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/get`, toSnakeCase(req));
    const respHump = toCamelCase(resp);
    return respHump.token;
  } catch (e) {
    logger.error('GetToken err', e);
    return '';
  }
};

export const parseBuglyToken = async (token: string) => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/parse`, { token });
    const respHump = toCamelCase(resp);
    return respHump.formList;
  } catch (e) {
    logger.error('ParseToken err', e);
    return {};
  }
};

const isEmptyTags = (tags = []) => tags.filter(tag => !!tag).length === 0;

export const formatCid = (cId: string) => cId?.replaceAll(':', '');

export const getIssueListToken = async (params: any) => {
  try {
    const formList = generateFormList(params);
    const filters = formList?.filters ?? [];
    // eslint-disable-next-line no-nested-ternary
    const tags = !isEmptyTags(params.newTagList) ? params.newTagList
      : (params.tagColor ? await getTagParams(params.newTagList!, params.tagColor!) : []);


    const req = {
      formList: {
        ...params,
        filters,
        tags: tags?.filter?.((item: string) => !!item),
      },
    };
    if (params?.[BuglyFiltersName.IssueStatus]) {
      req.formList[BuglyFiltersName.IssueStatus] = Array.isArray(params?.[BuglyFiltersName.IssueStatus])
        ? params?.[BuglyFiltersName.IssueStatus] : [params?.[BuglyFiltersName.IssueStatus]];
    }
    if (params.tapdStatus) {
      req.formList[BuglyFiltersName.TapdStatus] = Array.isArray(params.tapdStatus)
        ? params.tapdStatus : [params.tapdStatus];
    }
    if (params?.tapdHandler) {
      req.formList[BuglyFiltersName.TapdHandler] = Array.isArray(params?.tapdHandler)
        ? params.tapdHandler : [params.tapdHandler];
    }
    if (params.newTagList) {
      req.formList[BuglyFiltersName.TagList] = params.newTagList;
    }
    if (params.tagColor) {
      req.formList[BuglyFiltersName.TagColor] = params?.tagColor?.join?.(',');
    }

    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/get`, toSnakeCase(req));
    const respHump = toCamelCase(resp);
    return respHump.token;
  } catch (e) {
    logger.error('getIssueListToken err', e);
    return '';
  }
};


// 质量对比模式获取token
export const getCompareBuglyToken = async (params: any): Promise<string> => {
  try {
    const [filterA = [], filterB = []] = getCompareBuglyFilter(params);
    const topNum = params?.topData === FIELD_ALL ? undefined : Number(params.topData);
    if (params.tagColor) {
      // eslint-disable-next-line no-param-reassign
      params.tagColor = params?.tagColor?.join?.(',');
    }
    const req = {
      // 默认模式为关注条件A的过滤项
      formList: {
        ...params,
        tagColor: params.tagColor ? params?.tagColor?.join?.(',') : undefined,
        tapd_status: params.tapdStatus ? params.tapdStatus : [''],
        filters: filterA,
      },
      formListA: {
        filters: filterA,
        topNum,
        biz_extend_info: params?.bizExtendInfo?.length ? params?.bizExtendInfo : undefined,
      },
      formListB: {
        filters: filterB,
        topNum,
        biz_extend_info: params?.bizExtendInfoCompare?.length ? params?.bizExtendInfoCompare : undefined,
      },
    };
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/get`, toSnakeCase(req));
    const respHump = toCamelCase(resp);
    return respHump.token;
  } catch (e) {
    logger.error('getCompareBuglyToken err', e);
    return '';
  }
};

export const getTokenFromUrl = async () => {
  let token = getParamFromUrl('token');
  if (token) {
    return token;
  }
  // 使用 90 天时间兜底
  token = await getToken({
    ...formatTimeRange([
      moment().subtract(89, 'days')
        .format('YYYY-MM-DD'),
      moment().subtract(0, 'days')
        .format('YYYY-MM-DD'),
    ]),
  });
  return token;
};


const getCustomFilters = (fields: FieldList) => [
  ...(fields[FieldKey.UserCustom]?.map(item => ({
    ...item,
    name: toLineString(FieldKey.UserCustom),
  })) || []),
  ...((fields[FieldKey.BizExtendInfo] || [])?.map(item => ({
    ...item,
    name: toLineString(FieldKey.BizExtendInfo),
  }))),
  ...((fields[FieldKey.StandardTrafficSizeType] || [])?.map(item => ({
    ...item,
    name: toLineString(item.key!),
    values: item.operator !== 'BETWEEN'
      ? (item.values as string[])?.map(value => mb2Byte(value))
      : (item.values as string[])?.map(value => value.split(',').map((val) => {
        // Number('')会返回0，如果用户没填，入参也应该为空
        if (val === '') {
          return '';
        }
        return mb2Byte(val);
      })
        .join(',')),
  })))];

const getCustomCompareFilters = (fields: CompareFieldList) => (fields[FieldKey.UserCustomCompare]?.map(item => ({
  ...item,
  name: toLineString(FieldKey.UserCustom),
})) || [])
  .concat((fields[FieldKey.BizExtendInfoCompare]?.map(item => ({
    ...item,
    name: toLineString(FieldKey.BizExtendInfo),
  })) || []));

const getCompareFieldName = (fieldName: FieldKey) => `${toHumpString(fieldName)}Compare` as keyof CompareFieldList;

const getTestTagFilter = (fields: CompareFieldList, isCompare?: boolean) => {
  const isNewFilter = fields?.[FieldKey.StandardAbTestTag] || fields?.[FieldKey.StandardShiplyTag];
  if (!isNewFilter) {
    const shiplyTestTagValue = isCompare ? fields?.shiplyTagCompare : fields?.[FieldKey.ShiplyTag];
    const abTestTagValue = isCompare ? fields?.[FieldKey.AbTestTagCompare] : fields?.[FieldKey.AbTestTag];
    return [
      {
        name: FieldKey.ShiplyAbTestTag,
        key: getFilterKeyForCustomTag(FieldKey.ShiplyTag),
        values: toArray(shiplyTestTagValue ?? ''),
        operator: FieldsOperator.OR,
        type: KeyDataType.ARRAY,
      },
      {
        name: FieldKey.ShiplyAbTestTag,
        key: getFilterKeyForCustomTag(FieldKey.AbTestTag),
        values: toArray(abTestTagValue ?? ''),
        operator: FieldsOperator.OR,
        type: KeyDataType.ARRAY,
      },
    ];
  }

  const shiplyTestTag = fields?.[toHumpString(FieldKey.StandardShiplyTag) as keyof CompareFieldList]
    || fields?.[FieldKey.StandardShiplyTag];
  const abTestTag = fields?.[toHumpString(FieldKey.StandardAbTestTag) as keyof CompareFieldList]
    || fields?.[FieldKey.StandardAbTestTag];

  const shiplyTestTagValue = isCompare ? fields?.[getCompareFieldName(FieldKey.StandardShiplyTag)] : shiplyTestTag;
  const abTestTagValue = isCompare ? fields?.[getCompareFieldName(FieldKey.StandardAbTestTag)] : abTestTag;

  return [
    {
      name: FieldKey.ShiplyAbTestTag,
      key: getFilterKeyForCustomTag(FieldKey.ShiplyTag),
      values: toArray((shiplyTestTagValue?.values || '')),
      operator: shiplyTestTagValue?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.ShiplyAbTestTag,
      key: getFilterKeyForCustomTag(FieldKey.AbTestTag),
      values: toArray(abTestTagValue?.values || ''),
      operator: abTestTagValue?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
  ];
};

const genStandardCompareFields = (fields: CompareFieldList, isCompare?: boolean) => {
  const genCompareFieldName = (fieldName: FieldKey) => (isCompare
    ? getCompareFieldName(fieldName)
    : toHumpString(fieldName) as keyof CompareFieldList);
  const timeCostFieldValue = fields?.[genCompareFieldName(FieldKey.StandardTimeCost)]?.value;
  let timeCostValue = undefined;
  if (Array.isArray(timeCostFieldValue)) {
    if (!isEmpty(timeCostFieldValue?.[0]) && !isEmpty(timeCostFieldValue?.[1])) {
      timeCostValue = `${timeCostFieldValue?.[0]},${timeCostFieldValue?.[1]}`;
    } else {
      timeCostValue = timeCostFieldValue.map(value => String(value));
    }
  }

  return [
    {
      name: FieldKey.TimeCost,
      values: timeCostValue ? toArray(timeCostValue) : undefined,
      operator: fields?.[genCompareFieldName(FieldKey.StandardTimeCost)]?.operator,
    },
    {
      name: FieldKey.Stage,
      values: toArray((fields?.[genCompareFieldName(FieldKey.StandardScene)]?.values ?? '') as string),
      operator: fields?.[genCompareFieldName(FieldKey.StandardScene)]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.SystemStack,
      values: toArray(fields?.[genCompareFieldName(FieldKey.StandardSystemStack)] ?? ''),
      operator: MatchMode.OR,
    },
    {
      name: FieldKey.MonitoredThreadName,
      values: toArray(fields?.[genCompareFieldName(FieldKey.StandardMonitoredThreadName)]?.values as string[]),
      operator: fields?.[genCompareFieldName(FieldKey.StandardMonitoredThreadName)]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.BundleId,
      values: toArray((fields?.[genCompareFieldName(FieldKey.StandardBundleId)]?.values ?? fields?.[FieldKey.BundleId] ?? '') as string),
      operator: fields?.[genCompareFieldName(FieldKey.StandardBundleId)]?.mode ??  MatchMode.OR,
    },
    {
      name: FieldKey.ChannelId,
      values: toArray((fields?.[genCompareFieldName(FieldKey.StandardChannelId)]?.values ?? fields?.[FieldKey.ChannelId] ?? '') as string),
      operator: fields?.[genCompareFieldName(FieldKey.StandardChannelId)]?.mode ??  MatchMode.OR,
    },
    {
      name: FieldKey.FuncName,
      values: toArray(fields?.[genCompareFieldName(FieldKey.StandardFuncName)]?.values as string[]),
      operator: fields?.[genCompareFieldName(FieldKey.StandardFuncName)]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Feature,
      values: toArray(fields?.[genCompareFieldName(FieldKey.StandardFeature)]?.values as string[]),
      operator: fields?.[genCompareFieldName(FieldKey.StandardFeature)]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ClientIdentify,
      values: toArray(fields?.[genCompareFieldName(FieldKey.StandardClientIdentify)]?.values as string[]),
      operator: fields?.[genCompareFieldName(FieldKey.StandardClientIdentify)]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ProcessLaunchId,
      values: toArray((fields?.[genCompareFieldName(FieldKey.StandardProcessLaunchId)]?.values ?? fields?.processLaunchId ?? '') as string),
      operator: fields?.[genCompareFieldName(FieldKey.StandardProcessLaunchId)]?.mode ??  MatchMode.OR,
    },
    {
      name: FieldKey.LaunchId,
      values: toArray((fields?.[genCompareFieldName(FieldKey.StandardLaunchId)]?.values ?? fields?.launchId ?? '') as string),
      operator: fields?.[genCompareFieldName(FieldKey.StandardLaunchId)]?.mode ??  MatchMode.OR,
    },
    {
      name: FieldKey.Qimei,
      values: toArray((fields?.[genCompareFieldName(FieldKey.StandardQimei)]?.values ?? []) as string[]),
      operator: fields?.[genCompareFieldName(FieldKey.StandardQimei)]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.AccountId,
      values: toArray((fields?.[genCompareFieldName(FieldKey.StandardAccountId)]?.values ?? []) as string[]),
      operator: fields?.[genCompareFieldName(FieldKey.StandardAccountId)]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Platform,
      values: toArray((fields?.[genCompareFieldName(FieldKey.StandardPlatform)]?.values ?? []) as string[]),
      operator: fields?.[genCompareFieldName(FieldKey.StandardPlatform)]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Model,
      values: toArray((fields?.[genCompareFieldName(FieldKey.StandardModel)]?.values ?? fields?.[FieldKey.Model] ?? '') as string),
      operator: fields?.[genCompareFieldName(FieldKey.StandardModel)]?.mode ??  MatchMode.OR,
    },
    {
      name: FieldKey.OsVersion,
      values: toArray((
        fields?.[genCompareFieldName(FieldKey.StandardOsVersion)]?.values
        ?? fields[genCompareFieldName(FieldKey.OsVersion)]?.values ?? ''
      ) as string),
      operator: fields?.[genCompareFieldName(FieldKey.StandardOsVersion)]?.mode
        ?? fields[genCompareFieldName(FieldKey.OsVersion)]?.operator ?? MatchMode.OR,
    },
    {
      name: FieldKey.ProcessName,
      values: toArray(fields?.[genCompareFieldName(FieldKey.StandardProcessName)]?.values as string[]),
      operator: fields?.[genCompareFieldName(FieldKey.StandardProcessName)]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Scenes,
      values: toArray((isCompare ? fields?.sceneCompare : fields?.scenes) || ''),
      operator: FieldsOperator.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.SdkVersion,
      values: toArray(fields?.[genCompareFieldName(FieldKey.StandardSdkVersion)]?.values as string[]),
      operator: fields?.[genCompareFieldName(FieldKey.StandardSdkVersion)]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Brand,
      values: toArray((fields?.[genCompareFieldName(FieldKey.StandardBrand)]?.values ?? fields?.brand ?? '') as string),
      operator: fields?.[genCompareFieldName(FieldKey.StandardBrand)]?.mode || MatchMode.OR,
    },
  ].concat(getTestTagFilter(fields, isCompare));
};

const getCompareFilters = (fields: CompareFieldList) => {
  const customFilters = getCustomFilters(fields);
  const customCompareFilters = getCustomCompareFilters(fields);
  let timeRangeCompare;
  if (fields.customDateCompare) {
    const dateRange = getDateRangeByShortCut(fields.customDateCompare);
    timeRangeCompare = [
      dateRange[0].unix().toString(),
      dateRange[1].unix().toString(),
    ];
  } else {
    timeRangeCompare = [
      moment(fields.timeRangeCompare?.[0])
        .unix()
        .toString(),
      moment(fields.timeRangeCompare?.[1])
        .unix()
        .toString(),
    ];
  }

  const getOperator = (name: string) => {
    const map: Record<string, string> = {
      start_time: 'GE',
      start_time_compare: 'GE',
      end_time: 'LE',
      end_time_compare: 'LE',
      cost_time_avg_min: 'GE',
      cost_time_avg_max: 'LE',
      leak_bytes_min: 'GE',
      leak_bytes_max: 'LE',
      func_name: 'MATCH',
      model: 'OR',
      build_number: 'OR',
      channel_id: 'OR',
    };
    return map[name] || '';
  };

  const getName = (name: string) => {
    const isOccur = fields.timeType === 'occur';
    const isOccurCompare = fields.timeTypeCompare === 'occur';
    if (name === 'start_time' || name === 'end_time') {
      return isOccur ? 'report_time' : 'event_time';
    }
    if (name === 'start_time_compare' || name === 'end_time_compare') {
      return isOccurCompare ? 'report_time' : 'event_time';
    }
    return name;
  };

  const getScopes = (scopes: {name: FieldKey, valueKey: FieldKey,
    index: number, operator: NumberOperator, toByte?: boolean}[]) => scopes.map(item => ({
    name: toLineString(item.name),
    values: field2Filter(fields, item.valueKey, item.index, item.toByte),
    operator: item.operator,
  }));

  const standardMode = has(fields, FieldKey.StandardOsVersion);
  const filtersCompare: Record<string, any> = [
    {
      start_time: fields?.startTime,
      end_time: fields?.endTime,
      [FieldKey.Ground]: fields?.[FieldKey?.StandardGround] ?? fields?.ground,
      [FieldKey.AppVersionMode]: fields?.[FieldKey?.StandardVersionMode] ?? fields?.appVersionMode,
      ...(!standardMode ? {
        [FieldKey.Scene]: fields?.[FieldKey?.StandardScene]?.values ?? fields?.scene,
        [FieldKey.SdkVersion]: fields?.[FieldKey?.StandardSdkVersion]?.values ?? fields?.sdkVersion,
        [FieldKey.Model]: fields?.[FieldKey?.StandardModel]?.values ?? fields?.model,
        [FieldKey.LaunchId]: fields?.[FieldKey?.StandardLaunchId]?.values ?? fields?.launchId,
        [FieldKey.ChannelId]: fields?.[FieldKey?.StandardChannelId]?.values ?? fields?.channelId,
        [FieldKey.ProcessLaunchId]: fields?.[FieldKey?.StandardProcessLaunchId]?.values ?? fields?.processLaunchId,
        account_id: fields?.[FieldKey?.StandardAccountId]?.values ?? fields?.accountId,
        qimei: fields?.[FieldKey?.StandardQimei]?.values ?? fields?.qimei,
        client_identify: fields?.[FieldKey?.StandardClientIdentify]?.values ?? fields?.clientIdentify,
        feature: fields?.[FieldKey?.StandardFeature]?.values ?? fields?.feature,
        func_name: fields?.[FieldKey?.StandardFuncName]?.values ?? fields?.funcName,
        process_name: fields?.[FieldKey?.StandardProcessName]?.values ?? fields?.processName,
        bundle_id: fields?.[FieldKey?.StandardBundleId]?.values ?? fields?.bundleId,
        build_number: fields?.[FieldKey?.StandardBuildNumber]?.values ?? fields?.buildNumber,
        rom_name: fields?.[FieldKey?.StandardRom]?.values ?? fields?.romName,
      } : {}),
      // fd
      fd_type: fields?.fdType,
      // 大图
      show_type: fields?.showType,
      activity_name: fields?.activityName,
    },
    {
      start_time_compare: timeRangeCompare[0],
      end_time_compare: timeRangeCompare[1],
      [FieldKey.Ground]: fields?.[FieldKey?.StandardGroundCompare] ?? fields?.groundCompare,
      [FieldKey.SdkVersion]: fields?.[FieldKey?.StandardSdkVersionCompare]?.values ?? fields?.sdkVersionCompare,
      ...(!standardMode ? {
        [FieldKey.Scene]: fields?.[FieldKey?.StandardSceneCompare]?.values ?? fields?.sceneCompare,
        [FieldKey.Model]: fields?.[FieldKey?.StandardModelCompare]?.values ?? fields?.modelCompare,
        [FieldKey.AppVersionMode]: fields?.[FieldKey?.StandardVersionModeCompare]?.values
        ?? fields?.appVersionModeCompare,
        [FieldKey.LaunchId]: fields?.[FieldKey?.StandardLaunchIdCompare]?.values ?? fields?.launchIdCompare,
        [FieldKey.ChannelId]: fields?.[FieldKey?.StandardChannelIdCompare]?.values ?? fields?.channelIdCompare,
        [FieldKey.ProcessLaunchId]: fields?.[FieldKey?.StandardProcessLaunchIdCompare]?.values
        ?? fields?.processLaunchIdCompare,
        account_id: fields?.[FieldKey?.StandardAccountIdCompare]?.values ?? fields?.accountIdCompare,
        qimei: fields?.[FieldKey?.StandardQimeiCompare]?.values ?? fields?.qimeiCompare,
        client_identify: fields?.[FieldKey?.StandardClientIdentifyCompare]?.values ?? fields?.clientIdentifyCompare,
        feature: fields?.[FieldKey?.StandardFeatureCompare]?.values ?? fields?.featureCompare,
        func_name: fields?.[FieldKey?.StandardFuncNameCompare]?.values ?? fields?.funcNameCompare,
        process_name: fields?.[FieldKey?.StandardProcessNameCompare]?.values ?? fields?.processNameCompare,
        bundle_id: fields?.[FieldKey?.StandardBundleIdCompare]?.values ?? fields?.bundleIdCompare,
        build_number: fields?.[FieldKey?.StandardBuildNumberCompare]?.values ?? fields?.buildNumberCompare,
        rom_name: fields?.[FieldKey?.StandardRomCompare]?.values ?? fields?.romNameCompare,
      } : {}),
      // fd
      fd_type: fields?.fdTypeCompare,
      // 大图
      show_type: fields?.showTypeCompare,
      activity_name: fields?.activityNameCompare,
    },
  ].map((filters: Record<string, any>) => Object.keys(filters).map(item => ({
    name: getName(item),
    values: toArray(filters[item]) || [],
    operator: getOperator(item),
  }))
    .filter(item => isValidFilter(item)));

  const timeCostAvg = (fields.costTimeAvgMin && fields.costTimeAvgMax)
    ? [fields.costTimeAvgMin, fields.costTimeAvgMax] : [];

  const timeCostAvgCompare = (fields.costTimeAvgMinCompare && fields.costTimeAvgMaxCompare)
    ? [fields.costTimeAvgMinCompare, fields.costTimeAvgMaxCompare] : [];

  return [
    [...filtersCompare[0].concat([
      appVersionField2Filter(fields, false),
      ...(standardMode ? genStandardCompareFields(fields) : getTestTagFilter(fields, false) || []),
      ...[
        {
          name: FieldKey.TimeCostAvg,
          values: timeCostAvg,
          operator: MatchMode.Between,
        },
        {
          name: FieldKey.DetectStrategy,
          values: toArray(fields?.[FieldKey.StandardDetectStrategy]?.values || ''),
          operator: fields?.[FieldKey.StandardDetectStrategy]?.mode || '',
        },
        {
          name: FieldKey.TranslatedStacks,
          values: toArray(fields?.[FieldKey.StandardStack]?.values || ''),
          operator: fields?.[FieldKey.StandardStack]?.mode || '',
        },
        {
          name: FieldKey.StandardBuildNumber,
          values: toArray(fields?.[FieldKey.StandardBuildNumber]?.values || ''),
          operator: fields?.[FieldKey.StandardBuildNumber]?.mode || '',
        },
        {
          name: FieldKey.StandardRom,
          values: toArray(fields?.[FieldKey.StandardRom]?.values || ''),
          operator: fields?.[FieldKey.StandardRom]?.mode || '',
        },
        {
          name: fields?.[FieldKey.IsInferenceFeature] === MatchMode.NOT
            ? FieldKey.FeatureShow : FieldKey.IsInferenceFeature,
          values: fields?.[FieldKey.IsInferenceFeature]
            ? [fields?.[FieldKey.IsInferenceFeature] === MatchMode.OR ? '0' : '无堆栈问题']
            : [],
          operator: fields?.[FieldKey.IsInferenceFeature] || '',
        },
        ...getScopes([
          { name: FieldKey.DocCount, valueKey: FieldKey.DocCount, index: 0, operator: NumberOperator.GE,
            toByte: false },
          { name: FieldKey.DocCount, valueKey: FieldKey.DocCount, index: 1, operator: NumberOperator.LE,
            toByte: false },
          { name: FieldKey.MaxMallocSize, valueKey: FieldKey.MaxMallocSize, index: 0, operator: NumberOperator.GE },
          { name: FieldKey.MaxMallocSize, valueKey: FieldKey.MaxMallocSize, index: 1, operator: NumberOperator.LE },
          { name: FieldKey.AvgMaxMallocSize, valueKey: FieldKey.AvgMaxMallocSize, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.AvgMaxMallocSize, valueKey: FieldKey.AvgMaxMallocSize, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.AvgMallocSize, valueKey: FieldKey.AvgMallocSize, index: 0, operator: NumberOperator.GE },
          { name: FieldKey.AvgMallocSize, valueKey: FieldKey.AvgMallocSize, index: 1, operator: NumberOperator.LE },
          { name: FieldKey.QimeiCount, valueKey: FieldKey.QimeiCount, index: 0, operator: NumberOperator.GE,
            toByte: false },
          { name: FieldKey.QimeiCount, valueKey: FieldKey.QimeiCount, index: 1, operator: NumberOperator.LE,
            toByte: false },
          { name: FieldKey.P90MaxMallocSize, valueKey: FieldKey.P90MaxMallocSize, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.P90MaxMallocSize, valueKey: FieldKey.P90MaxMallocSize, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.P50MaxMallocSize, valueKey: FieldKey.P50MaxMallocSize, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.P50MaxMallocSize, valueKey: FieldKey.P50MaxMallocSize, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.MallocSize, valueKey: FieldKey.MallocSize, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.MallocSize, valueKey: FieldKey.MallocSize, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.UsedMem, valueKey: FieldKey.UsedMem, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.UsedMem, valueKey: FieldKey.UsedMem, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.P90MallocSize, valueKey: FieldKey.P90MallocSize, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.P90MallocSize, valueKey: FieldKey.P90MallocSize, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.P50MallocSize, valueKey: FieldKey.P50MallocSize, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.P50MallocSize, valueKey: FieldKey.P50MallocSize, index: 1,
            operator: NumberOperator.LE },
        ]),
      ].filter(item => item && !item?.values?.includes(FIELD_ALL) && item?.values?.length > 0),
      ...customFilters as any,
    ])].filter(item => !isEmpty(item) && !item?.values?.includes(FIELD_ALL) && item?.values?.length > 0),
    [...filtersCompare[1].concat([
      appVersionField2Filter(fields, true, true),
      ...(standardMode ? genStandardCompareFields(fields, true) : getTestTagFilter(fields, true) || []),
      ...[
        {
          name: FieldKey.TimeCostAvg,
          values: timeCostAvgCompare,
          operator: MatchMode.Between,
        },
        {
          name: FieldKey.DetectStrategy,
          values: toArray(fields?.[FieldKey.StandardDetectStrategyCompare]?.values || ''),
          operator: fields?.[FieldKey.StandardDetectStrategyCompare]?.mode || '',
        },
        {
          name: FieldKey.TranslatedStacks,
          values: toArray(fields?.[FieldKey.StandardStackCompare]?.values || ''),
          operator: fields?.[FieldKey.StandardStack]?.mode || '',
        },
        {
          name: FieldKey.StandardBuildNumber,
          values: toArray(fields?.[FieldKey.StandardBuildNumberCompare]?.values || ''),
          operator: fields?.[FieldKey.StandardBuildNumberCompare]?.mode || '',
        },
        {
          name: FieldKey.StandardRom,
          values: toArray(fields?.[FieldKey.StandardRom]?.values || ''),
          operator: fields?.[FieldKey.StandardRom]?.mode || '',
        },
        {
          name: fields?.[FieldKey.IsInferenceFeatureCompare] === MatchMode.NOT
            ? FieldKey.FeatureShow : FieldKey.IsInferenceFeature,
          values: fields?.[FieldKey.IsInferenceFeatureCompare]
            ? [fields?.[FieldKey.IsInferenceFeatureCompare] === MatchMode.OR ? '0' : '无堆栈问题']
            : [],
          operator: fields?.[FieldKey.IsInferenceFeatureCompare] || '',
        },
        ...getScopes([

          { name: FieldKey.DocCount, valueKey: FieldKey.DocCountCompare, index: 0, operator: NumberOperator.GE,
            toByte: false },
          { name: FieldKey.DocCount, valueKey: FieldKey.DocCountCompare, index: 1, operator: NumberOperator.LE,
            toByte: false },
          { name: FieldKey.MaxMallocSize, valueKey: FieldKey.MaxMallocSizeCompare, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.MaxMallocSize, valueKey: FieldKey.MaxMallocSizeCompare, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.AvgMaxMallocSize, valueKey: FieldKey.AvgMaxMallocSizeCompare, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.AvgMaxMallocSize, valueKey: FieldKey.AvgMaxMallocSizeCompare, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.AvgMallocSize, valueKey: FieldKey.AvgMallocSizeCompare, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.AvgMallocSize, valueKey: FieldKey.AvgMallocSizeCompare, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.QimeiCount, valueKey: FieldKey.QimeiCountCompare, index: 0, operator: NumberOperator.GE,
            toByte: false },
          { name: FieldKey.QimeiCount, valueKey: FieldKey.QimeiCountCompare, index: 1, operator: NumberOperator.LE,
            toByte: false },
          { name: FieldKey.P90MaxMallocSize, valueKey: FieldKey.P90MaxMallocSizeCompare, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.P90MaxMallocSize, valueKey: FieldKey.P90MaxMallocSizeCompare, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.P50MaxMallocSize, valueKey: FieldKey.P50MaxMallocSizeCompare, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.P50MaxMallocSize, valueKey: FieldKey.P50MaxMallocSizeCompare, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.MallocSize, valueKey: FieldKey.MallocSizeCompare, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.MallocSize, valueKey: FieldKey.MallocSizeCompare, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.UsedMem, valueKey: FieldKey.UsedMemCompare, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.UsedMem, valueKey: FieldKey.UsedMemCompare, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.P90MallocSize, valueKey: FieldKey.P90MallocSizeCompare, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.P90MallocSize, valueKey: FieldKey.P90MallocSizeCompare, index: 1,
            operator: NumberOperator.LE },
          { name: FieldKey.P50MallocSize, valueKey: FieldKey.P50MallocSizeCompare, index: 0,
            operator: NumberOperator.GE },
          { name: FieldKey.P50MallocSize, valueKey: FieldKey.P50MallocSizeCompare, index: 1,
            operator: NumberOperator.LE },

        ]),
      ].filter(item => isValidFilter(item)),
      ...customCompareFilters as any,
    ])].filter(item => isValidFilter(item)),
  ];
};

const field2Filter = (fields: any, fieldName: FieldKey, index: number, toByte = true) => {
  const convert = toByte ? mb2Byte : (v: string | number) => v;

  return toArray(String(fields?.[fieldName]?.[index]
    ? (convert(Number(fields?.[fieldName]?.[index]))) : ''));
};

export const appVersionField2Filter = (
  fields: CompareFieldList,
  isCompare?: boolean,
  next?: boolean,
): { name: string, values: string[], operator: string} => {
  // 非对比模式下app版本处理
  if (!isCompare && fields?.[FieldKey.StandardAppVersion]) {
    const operator = fields?.[FieldKey.StandardAppVersion]?.mode;
    const values = toArray(fields?.[FieldKey.StandardAppVersion]?.values as string);
    if (operator === AppVersionOperator.Between) {
      const value = (!isEmpty(values) && values[0] && values[1]) ? `${values?.[0]},${values?.[1]}` : '';
      return {
        name: FieldKey.AppVersion,
        values: toArray(value),
        operator,
      };
    }
    return {
      name: FieldKey.AppVersion,
      values,
      operator: operator || '',
    };
  }

  // 对比模式下app版本处理
  const versionField = toHumpString(FieldKey.StandardAppVersion) as keyof CompareFieldList;
  const compareVersionField = toHumpString(FieldKey.StandardAppVersionCompare) as keyof CompareFieldList;
  if (isCompare && (fields?.[versionField] || fields?.[compareVersionField])) {
    const prevValues = fields?.[versionField] || fields?.[FieldKey.StandardAppVersion];
    const operator = next
      ? fields?.[compareVersionField]?.mode
      : prevValues?.mode;
    const values = next
      ? toArray(fields?.[compareVersionField]?.values)
      : toArray(prevValues?.values);

    if (operator === MatchMode.Between) {
      // 两个版本都有值才传
      const value = (!isEmpty(values) && values[0] && values[1]) ? `${values?.[0]}, ${values?.[1]}` : '';
      return {
        name: FieldKey.AppVersion,
        values: toArray(value),
        operator,
      };
    }
    return {
      name: FieldKey.AppVersion,
      values,
      operator: operator || '',
    };
  }
  // 以上只针对新表单交互处理
  const operator = isCompare ? fields.appVersionOperatorCompare : fields.appVersionOperator;
  const minAppVersion = isCompare ? fields.minAppVersionCompare : fields.minAppVersion;
  const maxAppVersion = isCompare ? fields.maxAppVersionCompare : fields.maxAppVersion;
  const wildcardAppVersion = isCompare ? fields.wildcardAppVersionCompare : fields.wildcardAppVersion;
  const appVersion = isCompare ? fields.appVersionGroupCompare : fields.appVersionGroup;
  if (operator === AppVersionOperator.Between) {
    const values = (minAppVersion && maxAppVersion) ? `${minAppVersion},${maxAppVersion}` : '';
    return {
      name: FieldKey.AppVersion,
      values: toArray(values),
      operator,
    };
  }
  if (operator === AppVersionOperator.Wildcard) {
    const values = wildcardAppVersion?.replaceAll('*', '') ?? '';
    const operator = getWildcardOperator(wildcardAppVersion ?? '', false);

    return {
      name: FieldKey.AppVersion,
      values: toArray(values!),
      operator,
    };
  }
  return {
    name: FieldKey.AppVersion,
    values: toArray(appVersion!),
    operator: operator ?? '',
  };
};
//  特殊处理
const getFilePathFilters = (fields: FieldList) => [{
  name: FieldKey.FilePath,
  values: [''],
  operator: fields.filePath,
},
{
  name: FieldKey.OriginalStacks,
  values: [''],
  operator: fields?.[FieldKey.OriginalStacks],
},
{
  name: fields?.[FieldKey.IsInferenceFeature] === MatchMode.NOT
    ? FieldKey.FeatureShow : FieldKey.IsInferenceFeature,
  values: [fields?.[FieldKey.IsInferenceFeature] === MatchMode.OR ? '0' : '无堆栈问题'],
  operator: fields?.[FieldKey.IsInferenceFeature],
},
].filter(item => !!item.operator);


const getFilter = (fields: FieldList) => {
  const customFilters = getCustomFilters(fields);
  const filePathFilter = getFilePathFilters(fields);

  const timeCostFieldValue = fields?.[FieldKey.StandardTimeCost]?.value;
  const launchDurationFieldValue = fields?.[FieldKey.StandardLaunchDuration]?.value;
  const timeCostValue = (!isEmpty(timeCostFieldValue) && timeCostFieldValue?.[0] && timeCostFieldValue?.[1])
    ? `${timeCostFieldValue?.[0]},${timeCostFieldValue?.[1]}` : timeCostFieldValue;
  const launchDurationValue = (!isEmpty(launchDurationFieldValue)
    && launchDurationFieldValue?.[0] && launchDurationFieldValue?.[1])
    ? `${launchDurationFieldValue?.[0]},${launchDurationFieldValue?.[1]}` : launchDurationFieldValue;
  const standardTrafficSize = fields?.[FieldKey.TrafficSize] ?? {};
  const trafficSizeFilter = [];
  if (standardTrafficSize?.operator === MatchMode.Between) {
    trafficSizeFilter.push(
      {
        name: CommonFieldKey.TrafficSize,
        values: toArray(standardTrafficSize?.value?.[0] ? mb2Byte(standardTrafficSize?.value?.[0]) : ''),
        operator: 'GE',
      },
      {
        name: CommonFieldKey.TrafficSize,
        values: toArray(standardTrafficSize?.value?.[1] ? mb2Byte(standardTrafficSize?.value?.[1]) : ''),
        operator: 'LE',
      },
    );
  } else if (standardTrafficSize?.value?.length === 1) {
    trafficSizeFilter.push({
      name: CommonFieldKey.TrafficSize,
      values: toArray(standardTrafficSize?.value?.[0] ? mb2Byte(standardTrafficSize?.value?.[0]) : ''),
      operator: standardTrafficSize?.operator,
    });
  }

  const timeCostAvg = (fields.costTimeAvgMin && fields.costTimeAvgMax)
    ? [fields.costTimeAvgMin, fields.costTimeAvgMax] : [];

  return [
    ...(trafficSizeFilter || []),
    appVersionField2Filter(fields),
    fields[FieldKey.AppVersion],
    // new Filters
    {
      name: FieldKey.TimeCostAvg,
      values: timeCostAvg,
      operator: MatchMode.Between,
    },
    {
      name: FieldKey.SdkVersion,
      values: toArray(fields?.[FieldKey.StandardSdkVersion]?.values || fields?.sdkVersion),
      operator: fields?.[FieldKey.StandardSdkVersion]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.TranslatedStacks,
      values: toArray(fields?.[FieldKey.StandardStack]?.values || ''),
      operator: fields?.[FieldKey.StandardStack]?.mode,
    },
    {
      name: FieldKey.TagsInFilterUnderline,
      values: toArray((fields?.[FieldKey.StandardTagsInFilter]?.values ?? '') as string),
      operator: fields?.[FieldKey.StandardTagsInFilter]?.mode || MatchMode.OR,
      key: 'a13',
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.TimeCost,
      values: toArray(timeCostValue as string),
      operator: fields?.[FieldKey.StandardTimeCost]?.operator,
    },
    {
      name: FieldKey.LaunchDuration,
      values: toArray(launchDurationValue as string),
      operator: fields?.[FieldKey.StandardLaunchDuration]?.operator,
    },
    {
      name: FieldKey.Stage,
      values: toArray((fields?.[FieldKey.StandardScene]?.values ?? '') as string),
      operator: fields?.[FieldKey.StandardScene]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ShiplyAbTestTag,
      key: getFilterKeyForCustomTag(FieldKey.ShiplyTag),
      values: toArray((fields?.[FieldKey.StandardShiplyTag]?.values as string[]) ?? fields?.[FieldKey.ShiplyTag] ?? ''),
      operator: fields?.[FieldKey.StandardShiplyTag]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.ShiplyAbTestTag,
      key: getFilterKeyForCustomTag(FieldKey.AbTestTag),
      values: toArray((fields?.[FieldKey.StandardAbTestTag]?.values as string[]) ?? fields?.[FieldKey.AbTestTag] ?? ''),
      operator: fields?.[FieldKey.StandardAbTestTag]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.SystemStack,
      values: toArray(fields?.[FieldKey.StandardSystemStack] ?? ''),
      operator: MatchMode.OR,
    },
    {
      name: FieldKey.MonitoredThreadName,
      values: toArray(fields?.[FieldKey.StandardMonitoredThreadName]?.values as string[]),
      operator: fields?.[FieldKey.StandardMonitoredThreadName]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.BundleId,
      values: toArray((fields?.[FieldKey.StandardBundleId]?.values ?? fields?.[FieldKey.BundleId] ?? '') as string),
      operator: fields?.[FieldKey.StandardBundleId]?.mode ??  MatchMode.OR,
    },
    {
      name: FieldKey.ChannelId,
      values: toArray((fields?.[FieldKey.StandardChannelId]?.values as string[]) ?? fields?.channelId ?? ''),
      operator: fields?.[FieldKey.StandardChannelId]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.FuncName,
      values: toArray(fields?.[FieldKey.StandardFuncName]?.values ?? fields?.funcName as string),
      operator: fields?.[FieldKey.StandardFuncName]?.mode || MatchMode.MATCH,
    },
    {
      name: FieldKey.Feature,
      values: toArray(fields?.[FieldKey.StandardFeature]?.values as string[]),
      operator: fields?.[FieldKey.StandardFeature]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ClientIdentify,
      values: toArray(fields?.[FieldKey.StandardClientIdentify]?.values as string[]),
      operator: fields?.[FieldKey.StandardClientIdentify]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ProcessLaunchId,
      values: toArray((fields?.[FieldKey.StandardProcessLaunchId]?.values ?? fields?.processLaunchId ?? '') as string),
      operator: fields?.[FieldKey.StandardProcessLaunchId]?.mode ??  MatchMode.OR,
    },
    {
      name: FieldKey.LaunchId,
      values: toArray((fields?.[FieldKey.StandardLaunchId]?.values ?? fields?.launchId ?? '') as string),
      operator: fields?.[FieldKey.StandardLaunchId]?.mode ??  MatchMode.OR,
    },
    {
      name: FieldKey.Qimei,
      values: toArray(((fields?.[FieldKey.StandardQimei]?.values || fields?.[FieldKey.Qimei]) ?? []) as string[]),
      operator: fields?.[FieldKey.StandardQimei]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.DetectStrategy,
      values: toArray((fields?.[FieldKey.StandardDetectStrategy]?.values ?? [])),
      operator: fields?.[FieldKey.StandardDetectStrategy]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Rom,
      values: toArray((fields?.[FieldKey.StandardRom]?.values as string[]) ?? fields?.romName ?? ''),
      operator: fields?.[FieldKey.StandardRom]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.AccountId,
      values: toArray((fields?.[FieldKey.StandardAccountId]?.values ?? []) as string[]),
      operator: fields?.[FieldKey.StandardAccountId]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Platform,
      values: toArray((fields?.[FieldKey.StandardPlatform]?.values ?? []) as string[]),
      operator: fields?.[FieldKey.StandardPlatform]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Model,
      values: toArray((fields?.[FieldKey.StandardModel]?.values ?? fields?.[FieldKey.Model] ?? '') as string),
      operator: fields?.[FieldKey.StandardModel]?.mode ??  MatchMode.OR,
    },
    {
      name: FieldKey.OsVersion,
      values: toArray((fields?.[FieldKey.StandardOsVersion]?.values ?? fields[FieldKey.OsVersion]?.values ?? '') as string),
      operator: fields?.[FieldKey.StandardOsVersion]?.mode ?? fields[FieldKey.OsVersion]?.operator ?? MatchMode.OR,
    },
    {
      name: FieldKey.ProcessName,
      values: toArray(fields?.[FieldKey.StandardProcessName]?.values as string[]),
      operator: fields?.[FieldKey.StandardProcessName]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Scenes,
      values: toArray(fields?.scenes ?? ''),
      operator: FieldsOperator.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.SdkVersion,
      values: toArray(fields?.[FieldKey.StandardSdkVersion]?.values as string[]),
      operator: fields?.[FieldKey.StandardSdkVersion]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Brand,
      values: toArray((fields?.[FieldKey.StandardBrand]?.values ?? fields?.brand ?? '') as string),
      operator: fields?.[FieldKey.StandardBrand]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Ground,
      values: toArray((fields?.[FieldKey.StandardGround] ?? []) as string[]),
      operator: MatchMode.OR,
    },
    // new Filters end
    {
      name: fields.timeType === 'occur' ? 'report_time' : 'event_time',
      values: toArray(fields.startTime),
      operator: 'GE',
    },
    {
      name: toLineString(FieldKey.ParentTrafficSize),
      values: toArray(fields?.minParentTrafficSize ?? ''),
      operator: 'GE',
    },
    {
      name: toLineString(FieldKey.ParentTrafficSize),
      values: toArray(fields?.maxParentTrafficSize ?? ''),
      operator: 'LE',
    },
    {
      name: fields.timeType === 'occur' ? 'report_time' : 'event_time',
      values: toArray(fields.endTime),
      operator: 'LE',
    },
    {
      name: toLineString(FieldKey.CollectType),
      values: toArray(fields?.[FieldKey.CollectType] ?? ''),
      operator: '',
    },
    {
      name: toLineString(FieldKey.ExceptionType),
      values: toNumArray(fields?.[FieldKey.ExceptionType] ?? ''),
      operator: 'bitOr',
    },
    {
      name: toLineString(FieldKey.TrafficType),
      values: toArray(fields?.[FieldKey.TrafficType] ?? ''),
      operator: '',
    },
    {
      name: FieldKey.ShiplyAbTestTag,
      key: getFilterKeyForCustomTag(FieldKey.AbTestTag),
      values: toArray(fields?.[FieldKey.AbTestTag] ?? ''),
      operator: FieldsOperator.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: toLineString(FieldKey.AvgMaxMallocSize),
      values: toArray(String(fields?.[FieldKey.AvgMaxMallocSize]?.[0]
        ? (mb2Byte(fields?.[FieldKey.AvgMaxMallocSize]?.[0] as string)) : '')),
      operator: NumberOperator.GE,
    },
    {
      name: toLineString(FieldKey.AvgMaxMallocSize),
      values: toArray(String(fields?.[FieldKey.AvgMaxMallocSize]?.[1]
        ? (Number(fields?.[FieldKey.AvgMaxMallocSize]?.[1]) * 1024 * 1024) : '')),
      operator: NumberOperator.LE,
    },
    {
      name: toLineString(FieldKey.UsedMem),
      values: field2Filter(fields, FieldKey.UsedMem, 0),
      operator: NumberOperator.GE,
    },
    {
      name: toLineString(FieldKey.UsedMem),
      values: field2Filter(fields, FieldKey.UsedMem, 1),
      operator: NumberOperator.LE,
    },
    {
      name: toLineString(FieldKey.DocCount),
      values: field2Filter(fields, FieldKey.DocCount, 0, false),
      operator: NumberOperator.GE,
    },
    {
      name: toLineString(FieldKey.DocCount),
      values: field2Filter(fields, FieldKey.DocCount, 1, false),
      operator: NumberOperator.LE,
    },
    {
      name: toLineString(FieldKey.MaxMallocSize),
      values: field2Filter(fields, FieldKey.MaxMallocSize, 0),
      operator: NumberOperator.GE,
    },
    {
      name: toLineString(FieldKey.MaxMallocSize),
      values: field2Filter(fields, FieldKey.MaxMallocSize, 1),
      operator: NumberOperator.LE,
    },
    {
      name: toLineString(FieldKey.QimeiCount),
      values: field2Filter(fields, FieldKey.QimeiCount, 0, false),
      operator: NumberOperator.GE,
    },
    {
      name: toLineString(FieldKey.QimeiCount),
      values: field2Filter(fields, FieldKey.QimeiCount, 1, false),
      operator: NumberOperator.LE,
    },
    {
      name: toLineString(FieldKey.P90MaxMallocSize),
      values: field2Filter(fields, FieldKey.P90MaxMallocSize, 0),
      operator: NumberOperator.GE,
    },
    {
      name: toLineString(FieldKey.P90MaxMallocSize),
      values: field2Filter(fields, FieldKey.P90MaxMallocSize, 1),
      operator: NumberOperator.LE,
    },
    {
      name: toLineString(FieldKey.P50MaxMallocSize),
      values: field2Filter(fields, FieldKey.P50MaxMallocSize, 0),
      operator: NumberOperator.GE,
    },
    {
      name: toLineString(FieldKey.P50MaxMallocSize),
      values: field2Filter(fields, FieldKey.P50MaxMallocSize, 1),
      operator: NumberOperator.LE,
    },
    {
      name: toLineString(FieldKey.P90MallocSize),
      values: field2Filter(fields, FieldKey.P90MallocSize, 0),
      operator: NumberOperator.GE,
    },
    {
      name: toLineString(FieldKey.P90MallocSize),
      values: field2Filter(fields, FieldKey.P90MallocSize, 1),
      operator: NumberOperator.LE,
    },
    {
      name: toLineString(FieldKey.P50MallocSize),
      values: field2Filter(fields, FieldKey.P50MallocSize, 0),
      operator: NumberOperator.GE,
    },
    {
      name: toLineString(FieldKey.P50MallocSize),
      values: field2Filter(fields, FieldKey.P50MallocSize, 1),
      operator: NumberOperator.LE,
    },
    {
      name: toLineString(FieldKey.MallocSize),
      values: field2Filter(fields, FieldKey.MallocSize, 0),
      operator: NumberOperator.GE,
    },
    {
      name: toLineString(FieldKey.MallocSize),
      values: field2Filter(fields, FieldKey.MallocSize, 1),
      operator: NumberOperator.LE,
    },
    {
      name: toLineString(FieldKey.AvgMallocSize),
      values: toArray(String(fields?.[FieldKey.AvgMallocSize]?.[0]
        ? (Number(fields?.[FieldKey.AvgMallocSize]?.[0]) * 1024 * 1024) : '')),
      operator: NumberOperator.GE,
    },
    {
      name: toLineString(FieldKey.AvgMallocSize),
      values: toArray(String(fields?.[FieldKey.AvgMallocSize]?.[1]
        ? (Number(fields?.[FieldKey.AvgMallocSize]?.[1]) * 1024 * 1024) : '')),
      operator: NumberOperator.LE,
    },
    {
      name: FieldKey.ProblemType,
      values: toArray(fields[GroupName.problemType]!),
      operator: fields[GroupName.problemTypeFilter] || ComplianceFilter.Or,
    },
    {
      name: FieldKey.CoveredStatus,
      values: toArray(fields[GroupName.coveredStatus]!),
      operator: fields[GroupName.coveredStatusFilter] || ComplianceFilter.Or,
    },
    {
      name: FieldKey.PrivacyType,
      values: toArray(fields.privacyType!),
      operator: 'OR',
    },
    {
      name: FieldKey.PrivacyFunc,
      values: toArray(fields[GroupName.privacyFunction]!),
      operator: fields[GroupName.privacyFunctionFilter] || ComplianceFilter.Or,
    },
    {
      name: FieldKey.FuzzyMatch,
      values: toArray(fields[GroupName.fuzzyMatch]!),
      operator: fields[GroupName.fuzzyMatchFilter] || ComplianceFilter.Or,
    },
    {
      name: FieldKey.HostType,
      values: toArray(fields[GroupName.hostType]!),
      operator: fields[GroupName.hostTypeFilter] || ComplianceFilter.Or,
    },
    {
      name: FieldKey.BackTime,
      values: toArray(fields[FieldKey.BackTime]!),
      operator: fields[FieldKey.BackTimeFilterType] || 'GE',
    },
    {
      name: FieldKey.FilterSceneProblem,
      values: toArray(fields[FieldKey.FilterSceneProblem]!),
      operator: 'OR',
    },
    {
      name: FieldKey.Stack,
      values: toArray(fields[FieldKey.Stack]!),
      operator: 'OR',
    },
    {
      name: FieldKey.AppVersionMode,
      values: toArray((fields?.[FieldKey.StandardVersionMode] ?? fields.appVersionMode ?? '') as string),
      operator: MatchMode.OR,
    },
    {
      name: FieldKey.ExitReason,
      values: toArray(fields.exitReason!),
      operator: 'OR',
    },
    {
      name: FieldKey.ExitDescription,
      values: toArray(replaceWildcardValue(fields.exitDescription ?? '')),
      operator: getWildcardOperator(fields.exitDescription ?? ''),
    },
    {
      name: FieldKey.Operator,
      values: toArray(fields[FieldKey.Operator]!),
      operator: '',
    },
    {
      name: FieldKey.Area,
      values: toArray(fields[FieldKey.Area]!),
      operator: '',
    },
    {
      name: toLineString(FieldKey.FrontState),
      values: toArray(fields[FieldKey.FrontState]!),
      operator: '',
    },
    {
      name: toLineString(FieldKey.NetType),
      values: toArray(fields[FieldKey.NetType]!),
      operator: '',
    },
    {
      name: toLineString(FieldKey.ReportType),
      values: toArray(fields[FieldKey.ReportType]!),
      operator: '',
    },
    {
      name: FieldKey.Domain,
      values: toArray(fields[FieldKey.Domain]!),
      operator: '',
    },
    {
      name: toLineString(FieldKey.PeerName),
      values: toArray(fields[FieldKey.PeerName]!),
      operator: '',
    },
    {
      name: FieldKey.SmModule,
      values: toArray(fields?.smModule ?? ''),
      operator: 'MATCH',
    },
    {
      name: FieldKey.SmPermission,
      values: toArray(fields?.smPermission ?? ''),
      operator: 'MATCH',
    },
    {
      name: FieldKey.SmScene,
      values: toArray(fields?.smScene ?? ''),
      operator: 'MATCH',
    },
    {
      name: FieldKey.SmControl,
      values: toArray(fields?.smControl ?? ''),
      operator: 'MATCH',
    },
    {
      name: toLineString(FieldKey.LaunchCost),
      values: toArray(fields?.launchCost?.[0] ?? ''),
      operator: FieldsOperator.Ge,
    },
    {
      name: toLineString(FieldKey.LaunchCost),
      values: toArray(fields?.launchCost?.[1] ?? ''),
      operator: FieldsOperator.Le,
    },
    {
      name: toLineString(FieldKey.SpanCostTime),
      values: toArray(fields?.spanCost?.[0] ?? ''),
      operator: FieldsOperator.Ge,
    },
    {
      name: toLineString(FieldKey.SpanCostTime),
      values: toArray(fields?.spanCost?.[1] ?? ''),
      operator: FieldsOperator.Le,
    },
    {
      name: FieldKey.Span,
      values: fields?.spanNames ?? [''],
      operator: FieldsOperator.OR,
    },
    {
      name: FieldKey.LaunchType,
      values: fields?.launchType ?? [''],
      operator: FieldsOperator.OR,
    },
    {
      name: FieldKey.LaunchTag,
      values: fields?.launchTag ?? [''],
      type: KeyDataType.ARRAY,
      operator: fields?.tagFilter,
    },
    {
      name: toLineString(FieldKey.Linkages),
      values: fields?.linkages ?? [''],
      operator: FieldsOperator.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: toLineString(FieldKey.FdCount),
      values: fields?.[FieldKey.FdCount]?.value?.map?.(value => String(value)) ?? [''],
      operator: fields?.[FieldKey.FdCount]?.operator,
    },
    {
      name: toLineString(FieldKey.SuspiciousCount),
      values: fields?.[FieldKey.SuspiciousCount]?.value?.map?.(value => String(value)) ?? [''],
      operator: fields?.[FieldKey.SuspiciousCount]?.operator,
    },
    {
      name: toLineString(FieldKey.PowerExceptionType),
      values: fields?.exceptionTag ?? [''],
      operator: fields?.exceptionFilter,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.ViewTag,
      key: FieldKey.ViewTag,
      values: toArray(fields?.[FieldKey.ViewTag] ?? ''),
      operator: FieldsOperator.OR,
      type: KeyDataType.STRING,
    },
    {
      name: FieldKey.BuildNumber,
      values: toArray((fields?.[FieldKey.StandardBuildNumber]?.values) as string[] ?? fields?.buildNumber ?? ''),
      operator: fields?.[FieldKey.StandardBuildNumber]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Api,
      values: toArray(fields?.[FieldKey.Api] ?? ''),
      operator: FieldsOperator.OR,
    },
    {
      name: FieldKey.Perm,
      values: toArray(fields?.[FieldKey.Perm] ?? ''),
      operator: FieldsOperator.OR,
    },
    {
      name: FieldKey.Mime,
      values: toArray(fields?.[FieldKey.Mime] ?? ''),
      operator: FieldsOperator.OR,
    },
    {
      name: FieldKey.AttributionSdk,
      values: toArray(fields?.[FieldKey.AttributionSdk] ?? ''),
      operator: FieldsOperator.OR,
    },
    {
      name: 'inferred_labels',
      values: toArray((fields?.[FieldKey.StandardInferredLabels]?.values ?? '') as string),
      operator: fields?.[FieldKey.StandardInferredLabels]?.mode || MatchMode.MATCH,
      type: KeyDataType.ARRAY,
    },
    {
      name: AreaNameEnum.ClientCountry,
      values: toArray(fields?.[FieldKey.StandardArea]
        ?.find?.(item => item?.name === AreaNameEnum.ClientCountry)?.values),
      operator: FieldsOperator.OR,
    },
    {
      name: AreaNameEnum.ClientProvince,
      values: toArray(fields?.[FieldKey.StandardArea]
        ?.find?.(item => item?.name === AreaNameEnum.ClientProvince)?.values),
      operator: FieldsOperator.OR,
    },
    {
      name: AreaNameEnum.ClientCity,
      values: toArray(fields?.[FieldKey.StandardArea]
        ?.find?.(item => item?.name === AreaNameEnum.ClientCity)?.values),
      operator: FieldsOperator.OR,
    },
    {
      name: FieldKey.SdkVersion,
      values: toArray(fields?.[FieldKey.StandardSdkVersion]?.values || fields?.sdkVersion),
      operator: fields?.[FieldKey.StandardSdkVersion]?.mode || MatchMode.OR,
    },
  ].filter(item => isValidFilter(item))
    .concat(customFilters as any)
    .concat(filePathFilter as any);
};

// 为空和不为空 不需要传值
export const isValidFilter = (item: any) => item
    && !isEmpty(item)
    && ([
      MatchMode.ISEMPTY,
      MatchMode.NOTEMPTY,
    ].includes(item.operator) || (!item.values?.includes?.(FIELD_ALL)
      && item.values?.length > 0));

// 新加字段需要在这里配置转换关系
export const filterTransformMap: Record<string, string> = {
  productVersion: 'productVersion',
  osVersion: 'os',
  crashId: 'crashId',
  issueId: 'issueUuid',
  bundleId: 'bundleId',
  channelId: 'channelId',
  cpuArch: 'cpuArc',
  electronCpuArch: 'arch',
  hardware: 'model',
  contectInfo: 'accountId',
  deviceId: 'deviceId',
  processName: 'processName',
  stack: 'stack',
  threadName: 'threadName',
  deviceType: 'deviceType',
  cpuArchitecture: 'cpuArchitecture',
  appInBack: 'appInBack',
  platform: 'platform',
  launchDuration: 'launchDuration',
  freeMem: 'freeMem',
  rom: 'romName',
};


export const buglyFieldsTransformMap: Record<string, string> = {
  productVersion: 'version',
  os: 'osVersion',
  crashId: 'crashId',
  issueId: 'issueUuid',
  bundleId: 'bundleId',
  channelId: 'channelId',
  cpuArc: 'cpuArch',
  electronCpuArch: 'arch',
  model: 'hardware',
  accountId: 'contectInfo',
  deviceId: 'deviceId',
  processName: 'processName',
  stack: 'detail',
  threadName: 'threadName',
  deviceType: 'deviceType',
  cpuArchitecture: 'cpuArchitecture',
};

enum MatchRule {
  MATCH='MATCH',
  PREFIX='PREFIX',
  SUFFIX='SUFFIX'
}

const getMatchValue = (filter?: IFilter) => {
  if (filter?.operator === MatchRule.SUFFIX && filter?.values?.[0]) {
    return `*${filter?.values?.[0]}`;
  }
  if (filter?.operator === MatchRule.PREFIX && filter?.values?.[0]) {
    return `${filter?.values?.[0]}*`;
  }
  if (filter?.operator === MatchRule.MATCH && filter?.values?.[0]) {
    return `*${filter?.values?.[0]}*`;
  }
  return filter?.values?.[0];
};

const getBuglyFields = (fieldList: FieldList, compareBuglyFields?: boolean) => {
  if (!compareBuglyFields) return {};
  const buglyFields: Record<string, any> = {};
  Object.keys(buglyFieldsTransformMap).forEach((fieldName) => {
    const currentFilter = fieldList.filters?.find(item => item.name === toLineString(fieldName));
    // 版本是多选
    if (fieldName === 'productVersion') {
      buglyFields[buglyFieldsTransformMap[fieldName]] = currentFilter?.values || FIELD_ALL;
    } else if (fieldName === 'stack') {
      buglyFields[buglyFieldsTransformMap[fieldName]] = undefined;
    } else {
      buglyFields[buglyFieldsTransformMap[fieldName]] = getMatchValue(currentFilter) || FIELD_ALL;
    }
  });

  return buglyFields;
};

const parseRangeFilter = (
  fieldList: FieldList,
  filed: keyof FieldList,
  formatter?: (value: any) => string | number,
) => {
  const filterValueMin = fieldList.filters?.find(item => item.name === toLineString(filed)
    && item.operator === NumberOperator.GE)?.values?.[0] ?? '';
  const filterValueMax = fieldList.filters?.find(item => item.name === toLineString(filed)
    && item.operator === NumberOperator.LE)?.values?.[0] ?? '';

  const finalFormatter = formatter ? formatter : (value: string | number) => value;

  return fieldList?.[filed] || [
    filterValueMin !== '' ? finalFormatter(filterValueMin) : undefined,
    filterValueMax !== '' ? finalFormatter(filterValueMax) : undefined,
  ];
};

const byte2Mb = (value: number | string): number => (Number(value) / 1024 / 1024);

export const mb2Byte = (value: number | string): number => (Number(value) * 1024 * 1024);

const parseFilter = (fieldList: FieldList, compareBuglyFields?: boolean) => {
  // 根据electron环境动态选择字段名
  const clientCountry = isElectron() ? AreaNameEnum.ElectronClientCountry : AreaNameEnum.ClientCountry;
  const clientProvince = isElectron() ? AreaNameEnum.ElectronClientProvince : AreaNameEnum.ClientProvince;
  const clientCity = isElectron() ? AreaNameEnum.ElectronClientCity : AreaNameEnum.ClientCity;

  const standardArea = fieldList.filters?.filter(item => [
    clientCountry,
    clientProvince,
    clientCity].some(name => item.name === name));
  const standardTrafficSize = fieldList.filters?.filter(item => item.name === CommonFieldKey.TrafficSize);
  const buildNumber = fieldList.filters?.find(item => item.name === FieldKey.BuildNumber);
  const rom = fieldList.filters?.find(item => item.name === FieldKey.Rom);
  const appVersion = fieldList.filters?.find(item => item.name === FieldKey.AppVersion);
  const osVersion = fieldList.filters?.find(item => item.name === FieldKey.OsVersion);
  const problemType = fieldList.filters?.find(item => item.name === FieldKey.ProblemType);
  const coverStatus = fieldList.filters?.find(item => item.name === FieldKey.CoveredStatus);
  const privacyFunc = fieldList.filters?.find(item => item.name === FieldKey.PrivacyFunc);
  const privacyType = fieldList.filters?.find(item => item.name === FieldKey.PrivacyType);
  const fuzzyMatch = fieldList.filters?.find(item => item.name === FieldKey.FuzzyMatch);
  const hostType = fieldList.filters?.find(item => item.name === FieldKey.HostType);
  const appVersionMode = fieldList.filters?.find(item => item.name === FieldKey.AppVersionMode);
  const bundleId = fieldList.filters?.find(item => item.name === FieldKey.BundleId);
  const originalStacks = fieldList.filters?.find(item => item.name === FieldKey.OriginalStacks);
  const isInferenceFeature = fieldList.filters?.find(item => (item.name === FieldKey.IsInferenceFeature
    || FieldKey.FeatureShow === item.name));
  const model = fieldList.filters?.find(item => item.name === FieldKey.Model);
  const channelId = fieldList.filters?.find(item => item.name === FieldKey.ChannelId);
  const launchId = fieldList.filters?.find(item => item.name === FieldKey.LaunchId);
  const processLaunchId = fieldList.filters?.find(item => item.name === FieldKey.ProcessLaunchId);
  const exitReason = fieldList.filters?.find(item => item.name === FieldKey.ExitReason);
  const exitDescription = fieldList.filters?.find(item => item.name === FieldKey.ExitDescription);
  const filePath = fieldList.filters?.find(item => item.name === FieldKey.FilePath);
  const sdkVersion = fieldList.filters?.find(item => item.name === FieldKey.SdkVersion);
  const smModule = fieldList.filters?.find(item => item.name === FieldKey.SmModule);
  const smPermission = fieldList.filters?.find(item => item.name === FieldKey.SmPermission);
  const smScene = fieldList.filters?.find(item => item.name === FieldKey.SmScene);
  const smControl = fieldList.filters?.find(item => item.name === FieldKey.SmControl);

  const frontState = fieldList.filters?.find(item => item.name === toLineString(FieldKey.FrontState));
  const netType = fieldList.filters?.find(item => item.name === toLineString(FieldKey.NetType));
  const domain = fieldList.filters?.find(item => item.name === FieldKey.Domain);
  const reportType = fieldList.filters?.find(item => item.name === toLineString(FieldKey.ReportType));
  const operator = fieldList.filters?.find(item => item.name === FieldKey.Operator);
  const trafficExceptionType = fieldList.filters?.find(item => item.name === toLineString(FieldKey.ExceptionType));
  const collectType = fieldList.filters?.find(item => item.name === toLineString(FieldKey.CollectType));
  const trafficType = fieldList.filters?.find(item => item.name === toLineString(FieldKey.TrafficType));

  const brand = fieldList.filters?.find(item => item.name === FieldKey.Brand);
  const area = fieldList.filters?.find(item => item.name === FieldKey.Area);
  const linkages = fieldList.filters?.find(item => item.name === FieldKey.Linkages);
  const fdCount = fieldList.filters?.find(item => item.name === toLineString(FieldKey.FdCount));
  const suspiciousCount = fieldList.filters?.find(item => item.name === toLineString(FieldKey.SuspiciousCount));
  const scenes = fieldList.filters?.find(item => item.name === FieldKey.Scenes);
  // 电量异常类型
  const powerExceptionType = fieldList.filters?.find(item => item.name === toLineString(FieldKey.PowerExceptionType));

  // shiply标签和实验标签
  const shiplyTag = fieldList.filters?.find(item => item.key === 'a11');
  const abTestTag = fieldList.filters?.find(item => item.key === 'a12');

  // new Filters
  const standardDetectStrategy = fieldList.filters?.find(item => item.name === FieldKey.DetectStrategy);
  const standardGround = fieldList.filters?.find(item => item.name === FieldKey.Ground);
  const standardProcessName = fieldList.filters?.find(item => item.name === FieldKey.ProcessName);
  const standardPlatform = fieldList.filters?.find(item => item.name === FieldKey.Platform);
  const standardAccountId = fieldList.filters?.find(item => item.name === FieldKey.AccountId);
  const standardQimei = fieldList.filters?.find(item => item.name === FieldKey.Qimei);
  const standardLaunchId = fieldList.filters?.find(item => item.name === FieldKey.LaunchId);
  const standardClientIdentify = fieldList.filters?.find(item => item.name === FieldKey.ClientIdentify);
  const standardFeature = fieldList.filters?.find(item => item.name === FieldKey.Feature);
  const standardFuncName = fieldList.filters?.find(item => item.name === FieldKey.FuncName);
  const standardMonitorThreadName = fieldList.filters?.find(item => item.name === FieldKey.MonitoredThreadName);
  const standardSystemStack = fieldList.filters?.find(item => item.name === FieldKey.SystemStack);
  const standardStage = fieldList.filters?.find(item => item.name === FieldKey.Stage);

  const standardTimeCost = fieldList.filters?.find(item => item.name === FieldKey.TimeCost);
  const standardLaunchDuration = fieldList.filters?.find(item => item.name === FieldKey.LaunchDuration);
  const standardTagsInFilter = fieldList.filters?.find(item => item.name === FieldKey.TagsInFilterUnderline);


  const standardStack = fieldList.filters?.find(item => item.name === FieldKey.TranslatedStacks);

  const api = fieldList.filters?.find(item => item.name === FieldKey.Api);
  const perm = fieldList.filters?.find(item => item.name === FieldKey.Perm);
  const mime = fieldList.filters?.find(item => item.name === FieldKey.Mime);
  const attributionSdk = fieldList.filters?.find(item => item.name === FieldKey.AttributionSdk);
  const inferredLabels = fieldList.filters?.find(item => item.name === CommonFieldKey.StandardInferredLabels);
  return {
    [FieldKey.StandardArea]: standardArea?.length ? standardArea : undefined,
    [FieldKey.StandardInferredLabels]: {
      mode: inferredLabels?.operator || MatchMode.OR,
      values: inferredLabels?.values || undefined,
    },
    // new Filters
    [FieldKey.TrafficSize]: standardTrafficSize?.length === 2 ? {
      operator: MatchMode.Between,
      value: standardTrafficSize?.map(item => (item?.values?.[0] ? byte2Mb(item?.values?.[0]) : '')),
    } : {
      operator: standardTrafficSize?.[0]?.operator || MatchMode.Gt,
      value: standardTrafficSize?.[0]?.values?.[0] ? [byte2Mb(standardTrafficSize?.[0]?.values?.[0])] : undefined,
    },
    [FieldKey.StandardDetectStrategy]: {
      mode: standardDetectStrategy?.operator || MatchMode.OR,
      values: standardDetectStrategy?.values,
    },
    [FieldKey.StandardStack]: {
      mode: standardStack?.operator || MatchMode.OR,
      values: standardStack?.values ?? undefined,
    },
    [FieldKey.StandardTagsInFilter]: {
      mode: standardTagsInFilter?.operator || MatchMode.OR,
      values: standardTagsInFilter?.values ?? undefined,
    },
    [FieldKey.StandardTimeCost]: {
      operator: standardTimeCost?.operator,
      value: standardTimeCost?.operator === MatchMode.Between
        ? standardTimeCost?.values?.[0]?.split(',')
        : (standardTimeCost?.values ?? undefined),
    },
    [FieldKey.StandardLaunchDuration]: {
      operator: standardLaunchDuration?.operator,
      value: standardLaunchDuration?.operator === MatchMode.Between
        ? standardLaunchDuration?.values?.[0]?.split(',')
        : (standardLaunchDuration?.values ?? undefined),
    },
    [FieldKey.StandardShiplyTag]: {
      mode: shiplyTag?.operator || MatchMode.OR,
      values: shiplyTag?.values,
    },
    [FieldKey.StandardAbTestTag]: {
      mode: abTestTag?.operator || MatchMode.OR,
      values: abTestTag?.values,
    },
    [FieldKey.StandardAppVersion]: {
      mode: appVersion?.operator || MatchMode.OR,
      values: appVersion?.values?.[0]?.split(',') ?? undefined,
    },
    [FieldKey.StandardSystemStack]: standardSystemStack?.values ?? [FIELD_ALL],
    [FieldKey.StandardMonitoredThreadName]: {
      mode: standardMonitorThreadName?.operator || MatchMode.OR,
      values: standardMonitorThreadName?.values,
    },
    [FieldKey.StandardBundleId]: {
      mode: bundleId?.operator || MatchMode.OR,
      values: bundleId?.values,
    },
    [FieldKey.StandardChannelId]: {
      mode: channelId?.operator || MatchMode.OR,
      values: channelId?.values,
    },
    [FieldKey.StandardFuncName]: {
      mode: standardFuncName?.operator || MatchMode.OR,
      values: standardFuncName?.values,
    },
    [FieldKey.StandardFeature]: {
      mode: standardFeature?.operator || MatchMode.OR,
      values: standardFeature?.values,
    },
    [FieldKey.StandardClientIdentify]: {
      mode: standardClientIdentify?.operator || MatchMode.OR,
      values: standardClientIdentify?.values,
    },
    [FieldKey.StandardProcessLaunchId]: {
      mode: processLaunchId?.operator || MatchMode.OR,
      values: processLaunchId?.values,
    },
    [FieldKey.StandardLaunchId]: {
      mode: standardLaunchId?.operator || MatchMode.OR,
      values: standardLaunchId?.values,
    },
    [FieldKey.StandardQimei]: {
      mode: standardQimei?.operator || MatchMode.OR,
      values: standardQimei?.values,
    },
    [FieldKey.StandardAccountId]: {
      mode: standardAccountId?.operator || MatchMode.OR,
      values: standardAccountId?.values,
    },
    [FieldKey.StandardModel]: {
      mode: model?.operator || MatchMode.OR,
      values: model?.values,
    },
    [FieldKey.StandardOsVersion]: {
      mode: osVersion?.operator || MatchMode.OR,
      values: osVersion?.values,
    },
    [FieldKey.StandardPlatform]: {
      mode: standardPlatform?.operator || MatchMode.OR,
      values: standardPlatform?.values,
    },
    [FieldKey.StandardProcessName]: {
      mode: standardProcessName?.operator || MatchMode.OR,
      values: standardProcessName?.values,
    },
    [FieldKey.StandardSdkVersion]: {
      mode: sdkVersion?.operator || MatchMode.OR,
      values: sdkVersion?.values,
    },
    [FieldKey.StandardScene]: {
      mode: standardStage?.operator || MatchMode.OR,
      values: standardStage?.values,
    },
    [FieldKey.StandardBrand]: {
      mode: brand?.operator || MatchMode.OR,
      values: brand?.values,
    },
    [FieldKey.StandardRom]: {
      mode: rom?.operator || MatchMode.OR,
      values: rom?.values,
    },
    [FieldKey.StandardGround]: standardGround?.values || [FIELD_ALL],
    [FieldKey.StandardVersionMode]: appVersionMode?.values || [FIELD_ALL],
    [FieldKey.ShiplyTag]: shiplyTag?.values,
    [FieldKey.AbTestTag]: abTestTag?.values,
    [FieldKey.Scenes]: scenes?.values?.[0],
    [FieldKey.OriginalStacks]: originalStacks?.operator,
    [FieldKey.IsInferenceFeature]: isInferenceFeature?.operator,
    [FieldKey.ExceptionFilter]: powerExceptionType?.operator,
    [FieldKey.ExceptionTag]: powerExceptionType?.values,
    [FieldKey.AvgMaxMallocSize]: parseRangeFilter(fieldList, FieldKey.AvgMaxMallocSize, byte2Mb),
    [FieldKey.AvgMaxMallocSizeCompare]: fieldList?.avgMaxMallocSizeCompare,
    [FieldKey.AvgMallocSize]: parseRangeFilter(fieldList, FieldKey.AvgMallocSize, byte2Mb),

    // foom新加的字段
    [FieldKey.MaxMallocSize]: parseRangeFilter(fieldList, FieldKey.MaxMallocSize, byte2Mb),
    [FieldKey.MallocSize]: parseRangeFilter(fieldList, FieldKey.MallocSize, byte2Mb),
    [FieldKey.DocCount]: parseRangeFilter(fieldList, FieldKey.DocCount),
    [FieldKey.QimeiCount]: parseRangeFilter(fieldList, FieldKey.QimeiCount),
    [FieldKey.UsedMem]: parseRangeFilter(fieldList, FieldKey.UsedMem, byte2Mb),
    [FieldKey.P50MaxMallocSize]: parseRangeFilter(fieldList, FieldKey.P50MaxMallocSize, byte2Mb),
    [FieldKey.P90MaxMallocSize]: parseRangeFilter(fieldList, FieldKey.P90MaxMallocSize, byte2Mb),
    [FieldKey.P50MallocSize]: parseRangeFilter(fieldList, FieldKey.P50MallocSize, byte2Mb),
    [FieldKey.P90MallocSize]: parseRangeFilter(fieldList, FieldKey.P90MallocSize, byte2Mb),

    [FieldKey.AvgMallocSizeCompare]: fieldList?.avgMallocSizeCompare,
    [VersionSelectGroupName.appVersionGroup]: appVersion?.values || FIELD_ALL,
    [VersionSelectGroupName.appVersionOperator]: fieldList.appVersionOperator === AppVersionOperator.Wildcard
      ? AppVersionOperator.Wildcard : appVersion?.operator || AppVersionOperator.Or,
    [VersionSelectGroupName.minAppVersion]: appVersion?.operator === AppVersionOperator.Between ? appVersion?.values?.[0]?.split(',')[0] : undefined,
    [VersionSelectGroupName.maxAppVersion]: appVersion?.operator === AppVersionOperator.Between ? appVersion?.values?.[0]?.split(',')[1] : undefined,
    [VersionSelectGroupName.wildcardAppVersion]: fieldList?.wildcardAppVersion,
    [TimeRangeGroupName.timeQuick]: fieldList?.timeQuick || 'custom',
    [TimeRangeGroupName.timeType]: fieldList?.timeType || 'report',
    [FieldKey.AppVersion]: appVersion,
    [FieldKey.OsVersion]: osVersion,
    [FieldKey.Model]: fieldList[FieldKey.StandardModel] ? undefined : model?.values || FIELD_ALL, // 新UI防止重复提交
    channelId: channelId?.values || FIELD_ALL,
    [GroupName.problemType]: problemType?.values || FIELD_ALL,
    [GroupName.problemTypeFilter]: problemType?.operator || ComplianceFilter.Or,
    [GroupName.coveredStatus]: coverStatus?.values || FIELD_ALL,
    [GroupName.coveredStatusFilter]: coverStatus?.operator || ComplianceFilter.Or,
    [GroupName.privacyFunction]: privacyFunc?.values || FIELD_ALL,
    [GroupName.privacyFunctionFilter]: privacyFunc?.operator || ComplianceFilter.Or,
    [GroupName.fuzzyMatch]: fuzzyMatch?.values || FIELD_ALL,
    [GroupName.fuzzyMatchFilter]: fuzzyMatch?.operator || ComplianceFilter.Or,
    [GroupName.hostType]: hostType?.values || FIELD_ALL,
    [GroupName.hostTypeFilter]: hostType?.operator || ComplianceFilter.Or,
    [FieldKey.AttributionSdk]: attributionSdk?.values?.[0],
    ...toCamelCase({
      [FieldKey.BuildNumber]: buildNumber?.values || [],
      [FieldKey.Rom]: rom?.values || [],
      [FieldKey.ParentTrafficSize]: parseRangeFilter(fieldList, FieldKey.ParentTrafficSize, byte2Mb),
      [FieldKey.PrivacyType]: privacyType?.values || fieldList.privacyType || FIELD_ALL,
      [FieldKey.AppVersionMode]: appVersionMode?.values || fieldList.appVersionMode || FIELD_ALL,
      [FieldKey.BundleId]: fieldList[FieldKey.StandardBundleId]
        ? undefined : bundleId?.values || fieldList.bundleId || FIELD_ALL,
      [FieldKey.LaunchId]: launchId?.values?.[0],
      [FieldKey.ProcessLaunchId]: processLaunchId?.values?.[0],
      [FieldKey.ExitReason]: isIOS() || isVisionOS() || isAppleTV() ? exitReason?.values : exitReason?.values?.[0],
      [FieldKey.ExitDescription]: parseWildcardValue(exitDescription?.values?.[0] ?? '', exitDescription?.operator ?? ''),
      [FieldKey.FilePath]: filePath?.operator,
      [FieldKey.SdkVersion]: sdkVersion?.values?.[0] || fieldList.sdkVersion,
      // 流量
      [FieldKey.FrontState]: frontState?.values?.[0],
      [FieldKey.NetType]: netType?.values?.[0],
      [FieldKey.Domain]: domain?.values?.[0],
      [FieldKey.ReportType]: reportType?.values?.[0],

      [FieldKey.Operator]: operator?.values?.[0],
      [FieldKey.ExceptionType]: trafficExceptionType?.values?.[0] ?? '',
      [FieldKey.CollectType]: collectType?.values?.[0],
      [FieldKey.TrafficType]: trafficType?.values?.[0],

      [FieldKey.Brand]: brand?.values?.[0],
      [FieldKey.Area]: area?.values?.[0],
      [FieldKey.SmModule]: smModule?.values?.[0],
      [FieldKey.SmPermission]: smPermission?.values?.[0],
      [FieldKey.SmScene]: smScene?.values?.[0],
      [FieldKey.SmControl]: smControl?.values?.[0],
      [FieldKey.Linkages]: linkages?.values?.length ? linkages?.values : undefined,
      [FieldKey.FdCount]: fdCount?.values?.length ? {  operator: fdCount.operator,
        value: fdCount.values } : undefined,
      [FieldKey.SuspiciousCount]: suspiciousCount?.values?.length ? {  operator: suspiciousCount.operator,
        value: suspiciousCount.values } : undefined,
      [FieldKey.Api]: api?.values?.[0],
      [FieldKey.Perm]: perm?.values?.[0],
      [FieldKey.Mime]: mime?.values?.[0],
    }),
    // 兼容filter中的key转bugly的key
    ...getBuglyFields(fieldList, compareBuglyFields),
  };
};

const parseCompareFilter = (compareFieldList: any) => {
  const fields = toSnakeCase(compareFieldList);
  const appVersionCompare = fields[FieldKey.AppVersionCompare];
  const osVersionCompare = fields[FieldKey.OsVersionCompare];
  const costTime = compareFieldList?.[FieldKey.StandardTimeCost]?.value;
  const costTimeOperator = compareFieldList?.[FieldKey.StandardTimeCost]?.operator;
  const costTimeCompare = compareFieldList?.[FieldKey.StandardTimeCostCompare]?.value;
  const costTimeCompareOperator = compareFieldList?.[FieldKey.StandardTimeCostCompare]?.operator;

  return {
    [FieldKey.AppVersionCompare]: appVersionCompare,
    [FieldKey.OsVersionCompare]: osVersionCompare,
    // 卡顿
    costTimeAvg: { value: [compareFieldList.costTimeAvgMin, compareFieldList.costTimeAvgMax], operator: 'RANGE' },
    costTimeAvgCompare: { value: [compareFieldList.costTimeAvgMinCompare, compareFieldList.costTimeAvgMaxCompare], operator: 'RANGE' },
    costTime: { value: costTime, operator: costTimeOperator },
    costTimeCompare: { value: costTimeCompare, operator: costTimeCompareOperator },
    // java
    leakBytes: { value: [compareFieldList.leakBytesMin, compareFieldList.leakBytesMax], operator: 'RANGE' },
    leakBytesCompare: { value: [compareFieldList.leakBytesMinCompare, compareFieldList.leakBytesMaxCompare], operator: 'RANGE' },
  };
};

const parseCustomFilter = (fieldList: FieldList) => fieldList.filters?.
  filter(field => field.name === toLineString(FieldKey.UserCustom));

const parseCompareCustomFilter = (fieldList: FieldList) => fieldList.filters?.
  filter(field => field.name === toLineString(FieldKey.UserCustomCompare));

const parseBizFilter = (fieldList: FieldList) => fieldList.filters?.
  filter(field => field.name === toLineString(FieldKey.BizExtendInfo));

const parseCompareBizFilter = (fieldList: FieldList) => fieldList.filters?.
  filter(field => field.name === toLineString(FieldKey.UserCustomCompare));

export const getTagId = async (tags: string[]) => {
  const tagList = await getTagList();
  return tags.map(item => tagList?.find(tag => tag.tagName === item)?.tagId ?? '');
};

export const getRequestTagParams = async (tags?: string[], colors?: string[]) => {
  const tagList = await getTagList();
  const regTags = tags || [];
  const regColors = colors || [];
  const matchedTags = tagList.filter(tag => regTags.includes(tag.tagName ?? '')
    || regColors.includes(tag.tagColor ?? ''));

  return matchedTags.map(tag => tag.tagId);
};

export const getTagParams = async (
  tags: string[],
  tagColor: any,
): Promise<string[] | null>  => {
  const filterTag = tags?.filter?.(item => !!item);
  if (filterTag?.length === 0 && (!tagColor?.length || !tagColor?.[0])) {
    return [];
  }
  const tagList = await getTagList();
  if (filterTag?.length && (!tagColor?.length || !tagColor?.[0])) {
    return tags.map(item => tagList?.find(tag => tag.tagName === item)?.tagId ?? '');
  }
  if ((filterTag?.length === 0 || !filterTag) && tagColor?.length && tagColor?.[0]) {
    const filterTagList = tagList?.filter?.(item => item.tagColor && tagColor?.includes?.(item.tagColor));
    return filterTagList?.length !== 0 ? filterTagList?.map(item => item?.tagId ?? '') : null;
  }
  const isEveryColor = tags?.map(item => tagList.find(tag => tag.tagName === item)?.tagColor)
    ?.every(item => item && tagColor.includes(item));
  if (isEveryColor) {
    return tags.map(item => tagList?.find(tag => tag.tagName === item)?.tagId ?? '');
  }
  return null;
};


// 用于hooks获取token数据

export const parseIssueHooksToken = async (token: string) => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/parse`, { token });
    const respHump = toCamelCase(resp);
    const filter = parseFilter(respHump.formList!);
    const compareFilter = parseCompareFilter(respHump.formList!);
    const userCustomFilter = parseCustomFilter(respHump.formList!);
    const userCompareCustomFilter = parseCompareCustomFilter(respHump.formList!);
    const bizFilters = parseBizFilter(respHump.formList!);
    const bizFiltersCompare = parseCompareBizFilter(respHump.formList!);
    return deleteUndefinedOrNullProperties({
      ...respHump.formList,
      ...filter,
      ...compareFilter,
      userCustom: userCustomFilter,
      userCustomCompare: userCompareCustomFilter,
      bizExtendInfo: bizFilters,
      bizFiltersCompare,
    });
  } catch (e) {
    logger.error('parseCompareToken err', e);
    return {};
  }
};

export const getIssueHooksToken = async (params: FieldList): Promise<string> => {
  try {
    const filters = getFilter(params);
    const req = trpc.rmonitor.web_server.GetTokenReq.fromObject({
      formList: {
        ...params,
        filters,
        tagList: params.tags,
        tags: params.tags || params.tagColor ? await getTagParams(params.tags!, params.tagColor!) : [],
        tapdHandler: toArray(params.tapdHandler!),
      },
    });
    const parseField = {
      timeQuick: params.timeQuick,
      timeType: params.timeType,
      minAppVersion: params.minAppVersion,
      maxAppVersion: params.maxAppVersion,
      wildcardAppVersion: params.wildcardAppVersion,
      appVersionOperator: params.appVersionOperator,
      timeWindow: params.timeWindow,
    };
    const resp = await TRPCPost(`/${API_PREFIX}/api/url-token/get`, toSnakeCase({
      formList: {
        ...req.formList,
        ...parseField,
      },
    }));

    const respHump = trpc.rmonitor.web_server.GetTokenRsp.fromObject(toCamelCase(resp));
    return respHump.token;
  } catch (e) {
    logger.error('GetToken err', e);
    return '';
  }
};
